<script setup>
import { ref, reactive, onMounted, nextTick, watch, computed, onBeforeUnmount } from "vue";
import { CountTo } from "vue3-count-to";
import { mallOrderStateEnum } from "@/enum/mallOrderStateEnum.js";
import YTTablePagination from "@/components/YTTablePagination/YTTablePagination.vue";
import YTMallOrderItemState from "@/components/YTMallOrderItemState/YTMallOrderItemState.vue"
import moment from "moment";
import { userTokenStore } from "@/store/store.js";
import axios from "axios";
import { formatTags } from "@/hooks/tag.js";
import {disabledEndDate} from "@/hooks/dateUtils.js";
import { CaretTop, CaretBottom } from '@element-plus/icons-vue'
import {
  cancelMallGoodsOrderLogisticsShipments, detailMallGoodsOrder,
  existMallGoodsOrderLogisticsRecord,
  getMallGoodsOrderCount,
  listMallGoodsOrder, listMallGoodsOrderLogisticsRecord, shipmentsBatchMallGoodsOrderLogistics,
  shipmentsMallGoodsOrder
} from "@/api/mallGoods/mallGoods.js";
import { tenantTypeEnum } from "@/enum/tenantTypeEnum.js";
import {updateBatchOrderNo} from "@/api/mallGoods/mallGoods.js";
import {itemListIgnore} from "@/api/goods/ignireTenant.js";
import YTMallAnchor from "@/components/YTMallAnchor/index.vue";
import YTCard from "@/components/YTCard/newIndex.vue";

// 订单状态枚举
const mallOrderState = ref([
  {
    stateId: "ALL",
    stateName: "全部",
  },
  ...mallOrderStateEnum
])
// 合作身份枚举
const tenantTypeList = ref((tenantTypeEnum))

/*初次加载*/
onBeforeUnmount(() => {
  clearTimer()
})
onMounted(async () => {
  await handleSearch("btnSearch");

  startTimer()
});

/*统计数据相关功能*/
// 处理选中订单类型
const handleOrderTypeChange = (type) => {
  if (formData.orderState === type) return console.log("选择了相同的订单状态，不执行更改")
  if (type === 'AFTER_SALE') return ElMessage.warning("敬请期待~")
  formData.orderState = type
  handleSearch("btnSearch")
}
// 统计数据
const statisticsData = reactive({
  total: 0,
  unpaid: 0,
  paid: 0,
  timeoutPaid: 0,
  waitReceive: 0,
  afterSale: 0,
});
// 处理获取统计数据
const handleGetData = async () => {
  try {
    loading.value = true;
    const res = await getMallGoodsOrderCount();
    statisticsData.total = parseInt(res.total);
    statisticsData.unpaid = parseInt(res.unpaid);
    statisticsData.paid = parseInt(res.paid);
    statisticsData.timeoutPaid = parseInt(res.timeoutPaid);
    statisticsData.waitReceive = parseInt(res.waitReceive);
    statisticsData.afterSale = parseInt(res.afterSale);
    loading.value = false;
  } catch (e) {
    console.log(e);
    loading.value = false;
  }
};

/*过滤器相关功能*/
// 处理下单时间排序改变
const handleCrTimeOrderByChange = () => {
  formData.sortOrder = formData.sortOrder === 'ASC' ? 'DESC' : 'ASC'
  handleSearch("btnSearch")
}
// 过滤时间类型
const filterTimeType = [
  {
    label: "下单时间",
    value: "ORDER_TIME"
  },
  {
    label: "取消时间",
    value: "CANCEL_TIME"
  },
  {
    label: "发货时间",
    value: "SHIPPED_TIME"
  },
  {
    label: "完成时间",
    value: "COMPLETE_TIME"
  },
  {
    label: "退款时间",
    value: "REFUND_TIME"
  }
]
// 开始时间变化时重置结束时间
const handleStartTimeChange = (val, endTime) => {
  if (endTime && new Date(endTime) < new Date(val)) {
    formData.endDate = null; // 如果结束时间早于新开始时间，清空
  }
};
// 处理订单状态修改
const handleOrderStateChange = (e) => {
  console.log(e)
  handleSearch("btnSearch")
}
// 过滤器表单
const formData = reactive({
  orderNo: null,
  goodsName: null,
  logisticsTrackingNo: null,
  orderState: "ALL", // ALL:全部订单(提交时处理为null) UNPAID:待支付 PENDING_SHIPMENT:待发货 RECEIVING:待收货 AFTER_SALE:售后中 CANCELLED:已取消 COMPLETED:已完成
  sortOrder: "DESC",
  startDate: null,
  endDate: null,
  timeType: "ORDER_TIME",
  organizeType: null
});
// 根据表单搜索信息
const handleSearch = async (mode = "init") => {
  if (mode === "btnSearch") YTTablePaginationRef.value.resetPage();
  try {
    const page = {
      page: YTTablePaginationRef.value.getPage(),
      pageSize: YTTablePaginationRef.value.getPageSize(),
    };
    loading.value = true;
    let param = {
      ...formData,
      orderState: formData.orderState === 'ALL' ? null : formData.orderState
    }
    const res = await listMallGoodsOrder(page, param);
    tableData.value = formatOrderTableData(res.list || [])
    total.value = parseInt(res.total);
    loading.value = false;
    await handleGetData();
  } catch (e) {
    console.log(e);
    loading.value = false;
  }
};
// 处理表单内容为table设计的类型
const formatOrderTableData = (list = []) => {
  const result = []

  list.forEach(order => {
    result.push({
      rowType: 'header',
      orderNo: order.orderNo,
      orderTime: order.orderTime,
      deliveryTime: order.deliveryTime,
      completionTime: order.completionTime,
      cancelTime: order.cancelTime,
      logisticsTrackingNo: order.logisticsTrackingNo,
      logisticsCompanyName: order.logisticsCompanyName,
      deliveryType: order.deliveryType,
      orderState: order.orderState
    })

    result.push({
      rowType: 'order',
      ...order,
      orderItemList: order.orderItemList || []
    })
  })

  return result
}
// 清空表单内容
const handleReset = () => {
  formData.orderNo = null;
  formData.goodsName = null;
  formData.logisticsTrackingNo = null;
  formData.orderState = "ALL";
  formData.sortOrder = "DESC";
  formData.startDate = null;
  formData.endDate = null;
  formData.timeType = "ORDER_TIME";
  formData.organizeType = null;
  handleSearch("btnSearch");
};

/*表格相关功能*/
// 表格加载状态
const loading = ref(false);
// 表格数据
const tableData = ref([]);
// 表格数据总条数
const total = ref(0);
// 翻页组件锚点
const YTTablePaginationRef = ref();
// 处理翻页
const handleChangePage = () => {
  handleSearch();
};
// 处理下载“批量上传物流单号文件示例”
const handleDownLoadTempFile = () => {
  window.location.href = "https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/templateFiles/%E6%89%B9%E9%87%8F%E5%AF%BC%E5%85%A5%E5%BF%AB%E9%80%92%E5%8D%95%E5%8F%B7%E7%A4%BA%E4%BE%8B.xlsx"
}
// 处理导出发货订单
const handleDownloadShippedOrders = async () => {
  ElMessage.warning("下载中...");
  try {
    await downloadFile(
        `${
            import.meta.env.VITE_API_URL
        }/mall-goods/order/export`,
        {}, // 请求体数据
        `待发货订单-${moment().format(
            "YYYY-MM-DD_HHmmss"
        )}.xlsx` // 默认文件名
    );
  } catch (e) {
    console.log(e)
  }
};
// 处理点击批量上传物流单号按钮
const handleLogisticsBatchUpload = async () => {
  try {
    loading.value = true;
    const res = await existMallGoodsOrderLogisticsRecord()
    console.log("res", res)
    if (res) {
      openBatchDrawer.value = true
      nextTick(() => {
        getLogisticsInfo("btnSearch")
      })
    } else {
      openBatch.value = true
    }
    loading.value = false;
  } catch (e) {
    console.log(e)
    loading.value = false;
  }
}
// 物流记录列表数据
const drawerTableData = ref()
// 获取已上传物流单号信息
const getLogisticsInfo = async (mode = "init") => {
  if (mode === "btnSearch") YTTablePaginationDrawerRef.value.resetPage();
  try {
    drawerLoading.value = true
    let pageInfo = {
      page: YTTablePaginationDrawerRef.value.getPage(),
      pageSize: YTTablePaginationDrawerRef.value.getPageSize(),
    }
    const res = await listMallGoodsOrderLogisticsRecord(pageInfo, drawerForm)
    drawerTableData.value = []
    drawerTableData.value = res.list || []
    drawerTotal.value = parseInt(res.total);
    drawerLoading.value = false
  } catch(e) {
    console.log(e)
    drawerLoading.value = false
  }
}
/**
 * 自定义文件名的下载函数
 * @param {string} url - 完整接口URL
 * @param {Object} requestData - 请求体数据
 * @param {string} customFilename - 自定义文件名
 */
const downloadFile = async (url, requestData = {}, customFilename) => {
  try {
    const token = userTokenStore().token;
    const response = await axios.post(url, requestData, {
      responseType: "blob",
      headers: {
        Authorization: token.tokenValue,
        "Login-Type": token.loginType,
        "Content-Type": "application/json",
      },
    });

    const contentType = response.headers["content-type"] || ""

    // 如果返回的是 JSON，说明不是文件，而是错误信息
    if (contentType.includes("application/json")) {
      const text = await response.data.text()
      const json = JSON.parse(text)

      ElMessage.error(json.msg || "文件下载失败")
      return false
    }

    // 创建下载链接
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.setAttribute("download", customFilename); // 使用传入的自定义文件名
    document.body.appendChild(link);
    link.click();

    // 清理
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    }, 100);

    return true;
  } catch (error) {
    console.log("error", error);

    // 有后端响应
    if (error.response) {
      const contentType = error.response.headers["content-type"] || "";

      // 后端返回的是 JSON 错误信息
      if (contentType.includes("application/json")) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);

          ElMessage.error(json.msg || "文件下载失败");

          console.error("后端错误信息:", json);

          return false;
        } catch (e) {
          console.error("解析错误信息失败:", e);
        }
      }
    }

    console.error("文件下载失败:", error);
    ElMessage.error("文件下载失败");

    return false;
  }
};

// 买家/收货人展示状态
const isShowCustomerInfo = ref(false)
// 处理买家/收货人隐私信息展示
const handleShowPhone = () => {
  isShowCustomerInfo.value = !isShowCustomerInfo.value
}
// 处理订单行头
const spanMethod = ({ row, columnIndex }) => {
  if (row.rowType === 'header') {
    if (columnIndex === 0) {
      return [1, 11] // 11 总列数
    }
    return [0, 0]
  }
}
// 姓名脱敏
const maskName = (name) => {
  if (!name) return ''

  if (name.length === 1) return name

  return name[0] + '*'.repeat(name.length - 1)
}
// 手机号脱敏
const maskPhone = (phone) => {
  if (!phone) return ''
  return phone[0] + '******' + phone.slice(-3);
}
// 详细地址脱敏
const maskAddressDetail = (addressDetail) => {
  if (!addressDetail) return ''

  return addressDetail.slice(0, 3) + '******'
}
// 通用复制方法
const copyText = async (text) => {
  if (!text) {
    ElMessage.error('复制失败：目标文本为空')
    return false
  }

  try {
    // 优先现代 Clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(String(text))
    } else {
      // 兼容旧浏览器
      const textarea = document.createElement('textarea')

      textarea.value = String(text)
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'

      document.body.appendChild(textarea)

      textarea.select()

      document.execCommand('copy')

      document.body.removeChild(textarea)
    }

    ElMessage.success('复制成功')

    return true
  } catch (err) {
    console.error('复制失败：', err)

    ElMessage.error('复制失败')

    return false
  }
}
// 处理复制收货人信息
const handleCopy = async (item, isMask) => {
  const text = [
    `收件人：${isMask
        ? item.receiverInfo?.receiverName
        : maskName(item.receiverInfo?.receiverName) || ''}`,
    `电话：${isMask
        ? item.receiverInfo?.receiverPhone
        : maskPhone(item.receiverInfo?.receiverPhone) || ''}`,
    `地址：${isMask
        ? item.receiverInfo?.receiverAddress
        : maskAddressDetail(item.receiverInfo?.receiverAddress) || ''}`
  ]
      .filter(Boolean)
      .join('\n')

  await copyText(text)
}
// 处理对话框复制收货人信息
const handleDialogCopy = async (item) => {
  const text = [
    `收件人：${item.receiverInfo?.receiverName || ''}`,
    `电话：${item.receiverInfo?.receiverPhone || ''}`,
    `地址：${item.receiverInfo?.receiverAddress || ''}`
  ]
      .filter(Boolean)
      .join('\n')

  await copyText(text)
}
// 处理复制订单号/物流单号
const handleCopyTargetNo = async (targetNo) => {
  await copyText(targetNo)
}
// 处理表格商品名称拼接
const handleTableGoodsNameStr = (goods) => {
  let res = goods.goodsName
  goods.properties.map(property => {
    res += `[ ${property.groupName} : ${property.propertyName}]`
  })
  return res
}
// 公共处理时间
const getCountdownInfo = (item, type = 'orderState') => {
  if (!item) return null

  let endTime

  if (type === 'autoComplete') {
    if (!item.deliveryTime) return null
    endTime = moment(item.deliveryTime).add(10, 'days')
  } else if (item.orderState === 'UNPAID') {
    if (!item.orderTime) return null
    endTime = moment(item.orderTime).add(10, 'minutes')
  } else if (item.orderState === 'PENDING_SHIPMENT') {
    if (!item.paymentTime || item.minShipWithinHours == null) return null
    endTime = moment(item.paymentTime).add(item.minShipWithinHours, 'hours')
  } else {
    return null
  }

  const diff = nowTime.value.diff(endTime, 'seconds')
  const isOverdue = diff > 0
  const seconds = Math.abs(diff)

  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  return {
    isOverdue,
    text: days > 0
        ? `${days}天${hours}小时${minutes}分${secs}秒`
        : `${hours}小时${minutes}分${secs}秒`
  }
}
// 处理表格订单状态待发货时间计算
const getShipCountdown = (item) => {
  const info = getCountdownInfo(item, 'orderState')

  if (!info) return '-'

  if (item.orderState === 'PENDING_SHIPMENT') {
    return info.isOverdue
        ? `已超时${info.text}未发货`
        : `${info.text}内发货`
  }

  if (item.orderState === 'UNPAID') {
    return `${info.text}后取消`
  }

  return '-'
}
// 当前时间
const nowTime = ref(moment())
// 循环计时器
let timer = null
// 计时器开始
const startTimer = () => {
  clearTimer()

  timer = setInterval(() => {
    nowTime.value = moment()
  }, 1000)
}
// 清空计时器
const clearTimer = () => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}
// 批量发货文件本地文件变量
const uploadFile = ref(null)
const uploadRef = ref()
// 判断文件是否excel
const isExcelFile = (file) => {
  const fileName = file.name || ''
  const fileType = file.type || ''

  const isExcelType =
      fileType === 'application/vnd.ms-excel' ||
      fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

  const isExcelSuffix = /\.(xls|xlsx)$/i.test(fileName)

  return isExcelType || isExcelSuffix
}
// 处理批量发货文件修改
const handleFileChange = (file) => {
  const rawFile = file.raw

  if (!rawFile) return

  const isExcel = isExcelFile(rawFile)
  const isLt10M = rawFile.size / 1024 / 1024 <= 10

  if (!isExcel) {
    ElMessage.error('仅支持 xls、xlsx 格式')
    uploadFile.value = null
    uploadRef.value?.clearFiles()
    return
  }

  if (!isLt10M) {
    ElMessage.error('文件大小不能超过 10MB')
    uploadFile.value = null
    uploadRef.value?.clearFiles()
    return
  }

  uploadFile.value = rawFile
}
// 处理批量发货文件删除
const handleFileRemove = () => {
  uploadFile.value = null
  uploadRef.value?.clearFiles()
}
const handleFileExceed = () => {
  ElMessage.error('最多只能上传 1 个文件')
}
// 批量发货文件上传之前校验
const beforeUpload = (file) => {
  const isExcel =
      file.type === 'application/vnd.ms-excel' ||
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

  const isLt10M = file.size / 1024 / 1024 < 10

  if (!isExcel) {
    ElMessage.error('仅支持 xls、xlsx 格式')
    return false
  }

  if (!isLt10M) {
    ElMessage.error('文件大小不能超过 10MB')
    return false
  }

  return true
}
const formatFileSize = (size) => {
  if (!size && size !== 0) return '-'

  if (size < 1024) {
    return `${size}B`
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)}KB`
  }

  if (size < 1024 * 1024 * 1024) {
    return `${(size / 1024 / 1024).toFixed(2)}MB`
  }

  return `${(size / 1024 / 1024 / 1024).toFixed(2)}GB`
}

/*操作栏功能*/
// 发货表单锚点
const dialogFormRef = ref()
// 发货表单数据
const dialogForm = reactive({
  orderId: null,
  orderNo: null,
  orderItemList: [],
  receiverInfo: {
    receiverName: null,
    receiverPhone: null,
    receiverAddress: null
  },
  remark: null,
  deliveryTime: null,
  logisticsCompanyName: null,
  logisticsTrackingNo: null
})
// 表单校验规则
const dialogRules = {
  deliveryTime: [
    {required: true, message: "请选择发货时间", trigger: "blur"}
  ],
  logisticsCompanyName: [
    {required: true, message: "请选择配送快递", trigger: "blur"}
  ],
  logisticsTrackingNo: [
    {required: true, message: "请选择物流单号", trigger: "blur"}
  ]
}
// 对话框打开状态
const open = ref(false)
// 加载状态
const dialogLoading = ref(false)
// 处理对话框关闭
const handleClose = () => {
  open.value = false
  dialogForm.orderId = null
  dialogForm.orderNo = null
  dialogForm.orderItemList = []
  dialogForm.receiverInfo = {
    receiverName: null,
    receiverPhone: null,
    receiverAddress: null
  }
  dialogForm.remark = null
  dialogForm.deliveryTime = null
  dialogForm.logisticsCompanyName = null
  dialogForm.logisticsTrackingNo = null
  dialogFormRef.value.clearValidate()
}
// 处理对话框打开
const handleOpen = (item) => {
  console.log("item", item)
  dialogForm.orderId = item.orderId
  dialogForm.orderNo = item.orderNo
  dialogForm.orderItemList = item.orderItemList
  dialogForm.receiverInfo = item.receiverInfo
  dialogForm.remark = item.remark
  dialogForm.deliveryTime = moment().format('YYYY-MM-DD HH:mm:ss')
  open.value = true
}
// 物流公司枚举
const logisticsCompanyOptions = [
  {
    label: "顺丰速运",
    value: "顺丰速运"
  },
  {
    label: "中通快递",
    value: "中通快递"
  },
  {
    label: "圆通快递",
    value: "圆通快递"
  },
  {
    label: "申通快递",
    value: "申通快递"
  },
  {
    label: "韵达快递",
    value: "韵达快递"
  },
  {
    label: "极兔快递",
    value: "极兔快递"
  },
  {
    label: "EMS",
    value: "EMS"
  },
  {
    label: "邮政包裹",
    value: "邮政包裹"
  },
  {
    label: "德邦快递",
    value: "德邦快递"
  },
  {
    label: "京东快递",
    value: "京东快递"
  },
  {
    label: "其他",
    value: "其他"
  },
]
// 处理对话框提交表单
const handleSubmit = () => {
  dialogFormRef.value.validate(async (valid) => {
    if (valid) {
      try {
        let param = {
          deliveryTime: dialogForm.deliveryTime,
          orderId: dialogForm.orderId,
          logisticsCompanyName: dialogForm.logisticsCompanyName,
          logisticsTrackingNo: dialogForm.logisticsTrackingNo
        }
        await shipmentsMallGoodsOrder(param)
        ElMessage.success("发货成功")
        handleClose()
        handleSearch()
      } catch (e) {
        console.log(e)
      }
    } else {
      ElMessage.error("请根据提示修改表单信息")
    }
  })

}

/*批量发货相关*/
// 发货对话框打开状态
const openBatch = ref(false)
// 发货弹出抽屉打开状态
const openBatchDrawer = ref(false)
// 发货弹出抽屉缓存信息
const openBatchDrawerForm = ref()
// 批量发货弹窗加载状态
const batchLoading = ref(false)
// 处理批量发货对话框关闭
const handleBatchClose = () => {
  openBatch.value = false
}
// 处理批量发货点击确定
const handleBatchSubmit = async () => {
  try {
    if (!uploadFile.value) {
      ElMessage.warning('请先上传文件')
      return
    }
    batchLoading.value = true
    const formData = new FormData()
    formData.append('file', uploadFile.value)
    const res = await updateBatchOrderNo(formData)
    console.log(res)
    ElMessage.success(res)
    openBatch.value = false
    openBatchDrawer.value = true
    uploadRef.value?.clearFiles()
    uploadFile.value = null
    nextTick(() => {
      getLogisticsInfo("btnSearch")
    })
    batchLoading.value = false
  } catch (e) {
    console.log(e)
    ElMessage.error('批量上传物流单号失败')
    batchLoading.value = false
  }
}
// 处理发货弹出抽屉关闭
const handleCloseBatchDrawer = () => {
  console.log("关闭抽屉弹窗")
  openBatchDrawerForm.value = null
  openBatchDrawer.value = false
  drawerTableData.value = []
  drawerTotal.value = 0
  clearDrawerForm()
}
// 表头下单时间排序方式
const drawerOrderBy = ref("DESC")
// 处理弹出抽屉排序方式修改
const handleDrawerOrderByChange = () => {
  drawerOrderBy.value = drawerOrderBy.value === "DESC" ? "ASC" : "DESC";
}
// 表头昵称是否脱敏
const isShowNickname = ref(false)
// 处理昵称脱敏状态修改
const handleShowNickname = () => {
  isShowNickname.value = !isShowNickname.value
}
// 弹出抽屉翻页组件锚点
const YTTablePaginationDrawerRef = ref()
// 弹出抽屉总数
const drawerTotal = ref(0)
// 弹出抽屉表单
const drawerForm = reactive({
  orderNo: null,
  goodsId: null,
  goodsName: null,
  userNickname: null,
  userId: null,
  orderTimeStart: null,
  orderTimeEnd: null
})
// 弹窗抽屉加载状态
const drawerLoading = ref(false)
// 处理弹窗抽屉翻页
const handleDrawerPageChange = (e) => {
  console.log(e)
  getLogisticsInfo("btnSearch")
}
// 清空弹窗抽屉表单
const clearDrawerForm = async () => {
  drawerForm.orderNo = null
  drawerForm.goodsId = null
  drawerForm.goodsName = null
  drawerForm.userNickname = null
  drawerForm.userId = null
  drawerForm.orderTimeStart = null
  drawerForm.orderTimeEnd = null
}
// 弹窗抽屉重置过滤器
const handleDrawerReset = async () => {
  await clearDrawerForm()
  await getLogisticsInfo("btnSearch")
}
// 处理取消批量发货
const handleCancelBatchShipments = async () => {
  try {
    drawerLoading.value = true
    await cancelMallGoodsOrderLogisticsShipments()
    ElMessage.success("取消批量发货成功")
    openBatchDrawer.value = false
    clearDrawerForm()
    handleSearch()
    drawerLoading.value = false
  } catch (e) {
    console.log(e)
    drawerLoading.value = false
  }
}
// 处理弹窗抽屉开始时间改变
const handleDrawerStartTimeChange = (val, endTime) => {
  if (endTime && new Date(endTime) < new Date(val)) {
    drawerForm.orderTimeEnd = null; // 如果结束时间早于新开始时间，清空
  }
}
// 商品列表加载状态
const goodsLoading = ref(false)
// 处理弹窗抽屉商品名称改变
const handleDrawerGoodsChange = (e) => {
  console.log(e)
  const target = goodsList.value.find(i => i.goodsId === e)
  if (target === -1) return console.log("查询不到商品")
  drawerForm.goodsId = target.goodsId
  drawerForm.goodsName = target.goodsName
  getLogisticsInfo("btnSearch")
}
// 搜索关键字缓存
const keywordGoods = ref()
// 搜索触发函数
const handleRemoteSearch = (keyword) => {
  keywordGoods.value = keyword
  pageGoods.value = 1
  handleGetGoodsList()
}
// 商品列表总条数
const totalGoods = ref(0)
// 商品列表当前页数
const pageGoods = ref(1)
// 商品列表页长
const pageSizeGoods = ref(5)
// 商品列表
const goodsList = ref([])
// 获取商品列表
const handleGetGoodsList = async () => {
  goodsLoading.value = true
  const page = {
    page: pageGoods.value,
    pageSize: pageSizeGoods.value,
  }
  let param = {
    keyword: keywordGoods.value || null
  }
  const res = await itemListIgnore(page, param)
  goodsList.value = res.list
  totalGoods.value = parseInt(res.total)
  goodsLoading.value = false
}
// 处理商家翻页
const handleChangeGoodsPage = (e) => {
  pageGoods.value = e
  handleGetGoodsList()
}
// 处理确定批量发货
const handleSubmitBatchShipments = async () => {
  try {
    drawerLoading.value = true
    const res = await shipmentsBatchMallGoodsOrderLogistics()
    console.log("Res", res)
    ElMessage.success("批量发货成功")
    handleSearch()
    handleCloseBatchDrawer()
    drawerLoading.value = false
  } catch (e) {
    console.log(e)
    drawerLoading.value = false
  }
}

/*订单详情弹出抽屉相关*/
// 弹出抽屉打开状态
const openDetailDrawer = ref(false)
// 弹出抽屉加载状态
const detailDrawerLoading = ref(false)
// 处理点击订单详情
const handleClickDetail = (id) => {
  openDetailDrawer.value = true
  handleSearchDetail(id)
}
// 处理查询订单详情
const handleSearchDetail = async (id) => {
  try {
    detailDrawerLoading.value = true
    drawerDetailData.value = await detailMallGoodsOrder(id)
    handleTradeAnchor()
    isShowRefundInfo.value = drawerDetailData.value?.orderItemList.some(
      item => !!item.refundItem?.refundTime
    )
    detailDrawerLoading.value = false
  } catch (e) {
    console.log(e)
    detailDrawerLoading.value = false
  }
}
// 处理商品交易步骤条
const handleTradeAnchor = () => {
  // 下单时间 orderTime
  if (drawerDetailData.value.orderTime) {
    stepOptions.value[0].filled = true
    stepOptions.value[0].timePoint = moment(drawerDetailData.value.orderTime).format('YYYY-MM-DD HH:mm:ss')
  }
  // 付款时间 paymentTime
  if (drawerDetailData.value.orderState !== 'UNPAID') {
    if (drawerDetailData.value.paymentTime || drawerDetailData.value.payableAmount === 0) {
      stepOptions.value[1].filled = true
      stepOptions.value[1].timePoint = drawerDetailData.value.paymentTime ? moment(drawerDetailData.value.paymentTime).format('YYYY-MM-DD HH:mm:ss') : ""
    }
  }
  // 发货时间 deliveryTime
  if (drawerDetailData.value.deliveryType === "LOGISTICS") {
    if (drawerDetailData.value.deliveryTime) {
      stepOptions.value[2].filled = true
      stepOptions.value[2].timePoint = moment(drawerDetailData.value.deliveryTime).format('YYYY-MM-DD HH:mm:ss')
    }
  } else if (drawerDetailData.value.deliveryType === "NO_LOGISTICS") {
    stepOptions.value[2].filled = true
    stepOptions.value[2].timePoint = null
  }

  // 交易成功时间 completionTime
  if (drawerDetailData.value.completionTime) {
    stepOptions.value[3].filled = true
    stepOptions.value[3].timePoint = moment(drawerDetailData.value.completionTime).format('YYYY-MM-DD HH:mm:ss')
  }
}
// 重置商品交易步骤条
const resetTradeAnchor = () => {
  stepOptions.value.map(i => {
    i.filled = false
    i.timePoint = null
  })
}
// 处理弹出抽屉关闭
const handleCloseDetailDrawer = () => {
  resetTradeAnchor()
  drawerDetailData.value = {}
  isShowDetailNickname.value = false
  isShowDetailReceiverInfo.value = false
  isShowRefundInfo.value = false
}
// 弹出抽屉缓存信息
const drawerDetailData = ref({})
// 弹出抽屉买家昵称展示状态
const isShowDetailNickname = ref(false)
// 弹出抽屉收货信息展示状态
const isShowDetailReceiverInfo = ref(false)
// 是否展示退款原因/退款时间
const isShowRefundInfo = ref(false)
// 处理订单详情倒计时
const getDetailCountdown = (item) => {
  const info = getCountdownInfo(item, 'orderState')

  if (!info) return '-'

  return info.isOverdue
      ? `已超时${info.text}未发货`
      : `请在 ${info.text} 内发货`
}
// 处理订单完成倒计时
const getOrderCompletedCountdown = (item) => {
  if (
      item?.orderState !== 'SHIPPED' &&
      item?.orderState !== 'RECEIVING'
  ) {
    return '-'
  }

  const info = getCountdownInfo(item, 'autoComplete')

  if (!info) return '-'

  return `${info.text}后，订单会自动完成`
}
// 是否展示步骤条超时tag
const isShowOverdue = computed(() => {
  const info = getCountdownInfo(drawerDetailData.value, 'orderState')

  if (!info) return false

  return info.isOverdue && drawerDetailData.value?.orderState === 'PENDING_SHIPMENT'
})

// 步骤条配置
const stepOptions = ref([
  {
    href: "",
    title: "买家下单",
    filled: false,
    timePoint: null
  },
  {
    href: "",
    title: "买家付款",
    filled: false,
    timePoint: null
  },
  {
    href: "",
    title: "商家发货",
    filled: false,
    timePoint: null
  },
  {
    href: "",
    title: "交易成功",
    filled: false,
    timePoint: null
  }
])
</script>

<template>
  <div class="new_container_box">
    <div class="table_filter_box flex_left" style="margin-bottom: 18px;">
      <div class="table_filter_title">订单管理</div>
    </div>
    <div class="flex" v-loading="loading" style="margin-bottom: 18px;box-sizing: border-box;padding: 0 63px 0 39px;">
      <v-row justify="center">
        <v-col cols="2.4">
          <div :class="['info_item', {active: formData.orderState === 'ALL'}]" @click="handleOrderTypeChange('ALL')">
            <countTo
                :title="statisticsData.total"
                class="info_item_num txt_overflow"
                :startVal="0"
                :endVal="statisticsData.total"
                :duration="3000"
            ></countTo>
            <div class="info_item_title">全部订单</div>
          </div>
        </v-col>
        <v-col cols="2.4">
          <div :class="['info_item', {active: formData.orderState === 'UNPAID'}]" @click="handleOrderTypeChange('UNPAID')">
            <countTo
                :title="statisticsData.unpaid"
                class="info_item_num txt_overflow"
                :startVal="0"
                :endVal="statisticsData.unpaid"
                :duration="3000"
            ></countTo>
            <div class="info_item_title">待付款</div>
          </div>
        </v-col>
        <v-col cols="2.4">
          <div :class="['info_item', {active: formData.orderState === 'PENDING_SHIPMENT'}]" @click="handleOrderTypeChange('PENDING_SHIPMENT')">
            <countTo
                :title="statisticsData.paid"
                class="info_item_num txt_overflow"
                :startVal="0"
                :endVal="statisticsData.paid"
                :duration="3000"
            ></countTo>
            <div class="flex_align_t" style="gap: 10px;height: 16px;">
              <div class="info_item_title">待发货</div>
              <div class="info_item_error">
                超时未发货
                <countTo
                    :title="statisticsData.timeoutPaid"
                    class="txt_overflow"
                    :startVal="0"
                    :endVal="statisticsData.timeoutPaid"
                    :duration="3000"
                ></countTo>
              </div>
            </div>
          </div>
        </v-col>
        <v-col cols="2.4">
          <div :class="['info_item', {active: formData.orderState === 'RECEIVING'}]" @click="handleOrderTypeChange('RECEIVING')">
            <div>
              <countTo
                  :title="statisticsData.waitReceive"
                  class="info_item_num txt_overflow"
                  :startVal="0"
                  :endVal="statisticsData.waitReceive"
                  :duration="3000"
              ></countTo>
              <div class="info_item_title">待收货</div>
            </div>
          </div>
        </v-col>
        <v-col cols="2.4">
          <div :class="['info_item', {active: formData.orderState === 'AFTER_SALE'}]" @click="handleOrderTypeChange('AFTER_SALE')">
            <div>
              <countTo
                  :title="statisticsData.afterSale"
                  class="info_item_num txt_overflow"
                  :startVal="0"
                  :endVal="statisticsData.afterSale"
                  :duration="3000"
              ></countTo>
              <div class="info_item_title">售后中</div>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>
    <div class="mg_b_2" v-loading="loading" style="box-sizing: border-box;padding: 0 63px 0 39px;">
      <div class="filter_row flex mg_b_2" style="flex-wrap: wrap;gap: 10px;">
        <el-input
            v-model="formData.orderNo"
            :class="[
            formData.orderNo === null || formData.orderNo === ''
              ? 'input-null'
              : 'input-filled',
              'filter_input'
            ]"
        >
          <template #prefix>
            <div class="mg_r_1 filter_prefix">
              订单号
            </div>
          </template>
        </el-input>
        <el-input
            v-model="formData.goodsName"
            :class="[
            formData.goodsName === null || formData.goodsName === ''
              ? 'input-null'
              : 'input-filled',
              'filter_input'
            ]"
        >
          <template #prefix>
            <div class="mg_r_1 filter_prefix">
              商品名称
            </div>
          </template>
        </el-input>
        <el-input
            v-model="formData.logisticsTrackingNo"
            :class="[
            formData.logisticsTrackingNo === null || formData.logisticsTrackingNo === ''
              ? 'input-null'
              : 'input-filled',
              'filter_input'
            ]"
        >
          <template #prefix>
            <div class="mg_r_1 filter_prefix">
              物流单号
            </div>
          </template>
        </el-input>
        <div class="filter_time_group flex_center" style="height: 28px;">
          <el-select :disabled="true" class="filter_type filter_time_type" v-model="formData.timeType" placeholder="时间类型">
            <el-option
                class="filter_option"
                v-for="item in filterTimeType"
                :key="item.value"
                :label="item.label"
                :value="item.value"
            />
          </el-select>
          <el-date-picker
              :class="[
                formData.startDate === null || formData.startDate === ''
                ? 'input-null'
                : 'input-filled',
                'filter_start',
                'filter_date'
              ]"
              v-model="formData.startDate"
              type="datetime"
              placeholder="开始时间"
              format="YYYY-MM-DD HH:mm:ss"
              @change="(val) => handleStartTimeChange(val, formData.endDate)"
              :teleported="false"
              style="border-left: none"
          />
          <div class="filter_separator">~</div>
          <el-date-picker
              :class="[
            formData.endDate === null ||
            formData.endDate === ''
              ? 'input-null'
              : 'input-filled',
              'filter_end',
              'filter_date'
            ]"
            v-model="formData.endDate"
            type="datetime"
            placeholder="结束时间"
            format="YYYY-MM-DD HH:mm:ss"
            :disabled-date="(time) => disabledEndDate(time, formData.startDate)"
            :teleported="false"
          />
          <div class="filter_end_icon flex_center">
            <img class="cursor-pointer" style="width: 13px;height: 13px;" src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/default_calendar.svg" />
          </div>
        </div>
        <el-select
            :teleported="false"
            v-model="formData.orderState"
            :class="[
            formData.orderState === null || formData.orderState === ''
              ? 'input-null'
              : 'input-filled',
              'filter_order_status']"
            placeholder=""
            @change="handleOrderStateChange"
        >
          <template #prefix>
            <div class="filter_prefix">订单状态</div>
          </template>
          <el-option
              v-for="item in mallOrderState"
              :key="`mallOrderState${item.stateId}`"
              :label="item.stateName"
              :value="item.stateId"
          >
            <template #default>
              <div class="flex_bt">
                <div class="option-label filter_option">{{ item.stateName }}</div>
                <img
                    v-if="
                    formData.orderState === item.stateId
                  "
                    class="selected_icon"
                    src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/filter/filter/selected.svg"
                />
              </div>
            </template>
          </el-option>
        </el-select>
        <el-select
            :teleported="false"
            v-model="formData.organizeType"
            :class="[
            formData.organizeType === null || formData.organizeType === ''
              ? 'input-null'
              : 'input-filled',
              'filter_order_status']"
            placeholder=""
        >
          <template #prefix>
            <div class="filter_prefix">合作身份</div>
          </template>
          <el-option
              v-for="item in tenantTypeList"
              :key="`tenantTypeList${item.stateId}`"
              :label="item.stateName"
              :value="item.stateId"
          >
            <template #default>
              <div class="flex_bt">
                <div class="option-label filter_option">{{ item.stateName }}</div>
                <img
                    v-if="
                    formData.orderState === item.stateId
                  "
                    class="selected_icon"
                    src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/filter/filter/selected.svg"
                />
              </div>
            </template>
          </el-option>
        </el-select>
      </div>
      <div class="flex_left">
        <div class="flex mg_r_7">
          <v-btn
              style="margin-right: 15px;height:28px;font-size: 12px;"
              color="#4CAF50"
              @click="handleSearch('btnSearch')"
          >搜索</v-btn
          >
          <v-btn color="#d7d7df" style="color: #272539;height:28px;font-size: 12px;" @click="handleReset">重置</v-btn>
        </div>
        <div class="filter_total_num">
          共{{total}}条
        </div>
        <div class="filter_line"></div>
        <v-btn style="margin-right: 14px;height:28px;font-size: 12px;" color="#796CB4" @click="handleDownloadShippedOrders">导出待发货订单</v-btn>
        <v-btn color="#d7d7df" style="color: #272539;height:28px;font-size: 12px;" @click="handleLogisticsBatchUpload">批量上传物流单号</v-btn>
        <div class="filter_line"></div>
        <div class="flex cursor-pointer" @click="handleCrTimeOrderByChange">
          <div class="filter_orderTime">{{filterTimeType.find(i => i.value === formData.timeType).label}}</div>
          <div style="text-align: center">
            <div class="filter_orderTime_icon">
              <el-icon :color="formData.sortOrder === 'ASC' ? '#9c9c9c' : '#d5d5d5'" :size="15"><CaretTop /></el-icon>
            </div>
            <div class="filter_orderTime_icon">
              <el-icon :color="formData.sortOrder === 'DESC' ? '#9c9c9c' : '#d5d5d5'" :size="15"><CaretBottom /></el-icon>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div style="margin-bottom: 25px">
      <el-table
          :data="tableData"
          :border="false"
          style="width: 100%"
          :header-cell-style="{ background: '#F5F6F7', borderColor: '#DADDE2' }"
          :header-row-style="{ color: '#272539' }"
          :row-style="{ color: '#656565' }"
          v-loading="loading"
          :span-method="spanMethod"
          stripe
          class="outside_table"
      >
        <el-table-column
            prop="goodsName"
            label="商品信息"
            width="384"
            class-name="goods_cell_col"
        >
          <template #header="scope">
            <div style="padding-left: 47px">商品信息</div>
          </template>
          <template #default="scope">
            <div style="padding-left: 47px;flex: 1;" v-if="scope.row.rowType === 'header'" class="order_header">
              <span>平台订单号：{{ scope.row.orderNo }}</span>
              <img @click="handleCopyTargetNo(scope.row.orderNo)" class="cursor-pointer" style="width: 12px;height: 12px;margin-left: 6px;" src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/list_copy.svg" />
              <span style="margin-left: 39px;">下单时间：{{scope.row.orderTime ? moment(scope.row.orderTime).format("YYYY-MM-DD HH:mm:ss") : "—"}}</span>
              <span style="margin-left: 36px;" v-if="scope.row.orderState === 'SHIPPED' || scope.row.orderState === 'RECEIVING' || scope.row.orderState === 'COMPLETED'">物流单号：{{ scope.row.deliveryType === 'LOGISTICS' ? `${scope.row.logisticsCompanyName} ` + scope.row.logisticsTrackingNo : "无需物流" }}</span>
              <img @click="handleCopyTargetNo(scope.row.logisticsTrackingNo)" v-if="(scope.row.orderState === 'SHIPPED' || scope.row.orderState === 'RECEIVING' || scope.row.orderState === 'COMPLETED') && scope.row.deliveryType === 'LOGISTICS'" class="cursor-pointer" style="width: 12px;height: 12px;margin-left: 6px;" src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/list_copy.svg" />
              <span style="margin-left: 36px;" v-if="scope.row.orderState === 'SHIPPED' || scope.row.orderState === 'RECEIVING' || scope.row.orderState === 'COMPLETED'">发货时间：{{ scope.row.deliveryTime ? moment(scope.row.deliveryTime).format("YYYY-MM-DD HH:mm:ss") : "—"}}</span>
              <span style="margin-left: 36px;" v-if="scope.row.orderState === 'COMPLETED'">交易完成时间：{{scope.row.completionTime ? moment(scope.row.completionTime).format("YYYY-MM-DD HH:mm:ss") : "—" }}</span>
              <span style="margin-left: 36px;" v-if="scope.row.orderState === 'CANCELLED'">取消时间：{{ scope.row.cancelTime ? moment(scope.row.cancelTime).format("YYYY-MM-DD HH:mm:ss") : "—" }}</span>
            </div>

            <div v-else :class="['goods_list']" :style="{
              marginLeft: '29px',
              ...(scope.row.orderItemList?.length > 1
                ? {
                    borderLeft: '1px solid #F0F0F6',
                    paddingTop: '0',
                    marginTop: '10px',
                    paddingBottom: '0',
                    marginBottom: '10px'
                  }
                : {
                    borderLeft: 'none',
                    paddingTop: '10px',
                    marginTop: '0',
                    paddingBottom: '10px',
                    marginBottom: '0'
                  })
            }">
              <div
                  v-for="(goods, index) in scope.row.orderItemList"
                  :key="`orderItemListName${scope.$index}-${index}`"
                  :class="['goods_item', 'flex']"
                  :style="{paddingLeft: 0,
                  marginLeft: '10px'}"
              >
                <div style="width: 46px; height: 46px;margin-right: 15px;">
                  <el-image style="width: 46px; height: 46px;" :src="goods.goodsImage ? goods.goodsImage : ''" fit="contain" />
                </div>
                <div>
                  <el-tooltip
                      class="box-item"
                      effect="dark"
                      :content="handleTableGoodsNameStr(goods)"
                      placement="top"
                  >
                    <div class="goods_name">
                      {{ goods.goodsName }}
                      <span v-if="goods.properties.length > 0">
                        <span v-for="(property, propIndex) in goods.properties" :key="`properties-${property.propertyGroupId}-propertyName-${property.propertyGroupItemId}-${propIndex}`">
                          [{{ property.groupName }}:{{property.propertyName}}]
                        </span>
                      </span>
                    </div>
                  </el-tooltip>
                  <div class="flex_left" style="gap: 3px;flex-wrap: wrap">
                    <div class="goods_item_tag" v-for="(tag, tagIndex) in goods.tagList" :key="`orderItemListName${scope.$index}-${index}-tagList${tagIndex}`">
                      {{tag.tagName}}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </template>
        </el-table-column>
        <el-table-column
            prop=""
            label="单价"
            width="120"
            class-name="goods_cell_col"
        >
          <template #header>
            <div style="text-align: center">单价</div>
          </template>
          <template #default="scope">
            <div v-if="scope.row.rowType === 'header'" class="order_header">

            </div>
            <div v-else>
              <div class="goods_list" v-if="scope.row.orderItemList?.length > 0">
                <div class="goods_item flex_center" v-for="(item, index) in scope.row.orderItemList" :key="`orderItemListPrice${scope.$index}-${index}`">
                  <div>
                    <div class="table_price" style="margin-bottom: 6px;">会员价：￥{{item.retailPrice}}</div>
                    <div class="table_price">VIP价：￥{{item.vipPrice}}</div>
                  </div>
                </div>
              </div>
              <div v-else>—</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="下单数量" class-name="goods_cell_col" min-width="120">
          <template #header>
            <div style="text-align: center">下单数量</div>
          </template>
          <template #default="scope">
            <div v-if="scope.row.rowType === 'header'" class="order_header">

            </div>
            <div v-else>
              <div class="goods_list" v-if="scope.row.orderItemList?.length > 0">
                <div class="goods_item flex_center" v-for="(item, index) in scope.row.orderItemList" :key="`orderItemListQuantity${scope.$index}-${index}`">
                  <div class="table_quantity">×{{item.quantity}}</div>
                </div>
              </div>
              <div v-else>—</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column
            label="支付状态"
            width="80"
            :tooltip-formatter="formatTags"
            class-name="goods_cell_col"
        >
          <template #header>
            <div style="text-align: center">支付状态</div>
          </template>
          <template #default="scope">
            <div v-if="scope.row.rowType === 'header'" class="order_header">
            </div>
            <div v-else :class="['goods_list']" :style="{marginRight: '29px', borderRight: (scope.row.orderItemList?.length || 0) > 1 ? '1px solid #F0F0F6' : 'none'}">
              <div class="table_orderItemState goods_list" v-if="scope.row.orderItemList?.length > 0">
                <div class="goods_item flex_center" v-for="(item, index) in scope.row.orderItemList" style="padding-right: 0;margin-right: 10px;" :key="`orderItemListOrderItemState${scope.$index}-${index}`">
                  <div>
                    <YTMallOrderItemState :state="item.orderItemState"></YTMallOrderItemState>
                  </div>
                </div>
              </div>
              <div v-else>—</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="" label="实收金额" width="120">
          <template #header>
            <div style="text-align: center">实收金额</div>
          </template>
          <template #default="scope">
            <div v-if="scope.row.rowType === 'header'" class="order_header">
            </div>
            <div v-else>
              <div class="table_paymentType" v-if="scope.row.payment?.paymentMethod === 'WXPAY'">
                微信支付
              </div>
              <div class="table_paymentType" v-else-if="scope.row.payment?.paymentMethod === 'ALIPAY'">
                支付宝支付
              </div>
              <div class="table_paymentType" v-else>—</div>
              <div class="table_receivedAmount">
                ￥{{scope.row.receivedAmount}}
              </div>
              <div class="table_isFreeShipping">
                <div v-if="scope.row.deliveryType === 'LOGISTICS'">
                  {{scope.row.isFreeShipping ? '包邮' : '运费到付'}}
                </div>
                <div v-if="scope.row.deliveryType === 'NO_LOGISTICS'">
                  无需物流
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="" label="合作身份" width="120">
          <template #header>
            <div>合作身份</div>
          </template>
          <template #default="scope">
            <div v-if="scope.row.rowType === 'header'" class="order_header">
            </div>
            <div v-else>
              {{ scope.row.storeName }}
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="" label="所属商家" width="120">
          <template #header>
            <div>所属商家</div>
          </template>
          <template #default="scope">
            <div v-if="scope.row.rowType === 'header'" class="order_header">
            </div>
            <div v-else>
              {{ scope.row.tenantName }}
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="" label="买家/收货人" width="240">
          <template #header>
            <div class="flex_center flex_left cursor-pointer" @click="handleShowPhone">
              <div style="margin-right: 8px">买家/收货人</div>
              <img src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/default_eyes_open.svg" v-if="isShowCustomerInfo">
              <img src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/default_eyes_close.svg" v-else>
            </div>
          </template>
          <template #default="scope">
            <div v-if="scope.row.rowType === 'header'" class="order_header">
            </div>
            <div v-else>
              <div class="flex_center flex_left" style="margin-bottom: 8px;">
                <img style="width: 17px;height: 17px;border-radius: 50%;margin-right: 8px;" :src="scope.row.userAvatar ? scope.row.userAvatar : 'https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/default_avatar.svg'">
                <div class="table_nickname">{{scope.row.nickname}}</div>
              </div>
              <div class="flex_bt" style="margin-bottom: 4px;">
                <div>
                  <div class="table_customerName">{{isShowCustomerInfo ? scope.row.receiverInfo?.receiverName : maskName(scope.row.receiverInfo?.receiverName)}}&nbsp;{{isShowCustomerInfo ? `[` + (scope.row.receiverInfo?.receiverPhone || '') + `]` : `[` + maskPhone(scope.row.receiverInfo?.receiverPhone) + `]`}}</div>
                </div>
                <img class="cursor-pointer" style="width: 12px;height: 12px;" src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/list_copy.svg" @click="handleCopy(scope.row, isShowCustomerInfo)"/>
              </div>
              <div class="table_address">
                <div>{{isShowCustomerInfo ? scope.row.receiverInfo?.receiverAddress : maskAddressDetail(scope.row.receiverInfo?.receiverAddress)}}</div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="" label="备注" width="160">
          <template #default="scope">
            <div v-if="scope.row.rowType === 'header'" class="order_header">
            </div>
            <div v-else>
              <div class="table_remark" style="text-align: left">
                {{scope.row.remark ? scope.row.remark : "无"}}
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="" label="订单状态" width="110">
          <template #default="scope">
            <div v-if="scope.row.rowType === 'header'" class="order_header">
            </div>
            <div class="table_state" v-else>
              <div v-if="scope.row.orderState === 'PENDING_SHIPMENT'">待发货<span v-if="scope.row.isPartialRefund">&emsp;部分退款</span></div>
              <div v-if="scope.row.orderState === 'UNPAID'">待付款</div>
              <div v-if="scope.row.orderState === 'REFUNDED'">已退款</div>
              <div class="countdown" v-if="scope.row.orderState === 'PENDING_SHIPMENT' || scope.row.orderState === 'UNPAID'">{{ getShipCountdown(scope.row) }}</div>
              <div v-if="scope.row.orderState === 'RECEIVING' || scope.row.orderState === 'SHIPPED'">待收货<span v-if="scope.row.isPartialRefund">&emsp;部分退款</span></div>
              <div class="isSuccess" v-if="scope.row.orderState === 'COMPLETED'">已完成<span v-if="scope.row.isPartialRefund">&emsp;部分退款</span></div>
              <div class="isCancel" v-if="scope.row.orderState === 'CANCELLED'">已取消</div>
              <div class="isCancel" v-if="scope.row.orderState === 'CLOSED'">已关闭</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="control" label="操作" width="120" fixed="right">
          <template #default="scope">
            <div>
              <div>
                <div class="flex_center">
                  <v-btn v-if="scope.row.orderState === 'PENDING_SHIPMENT' && scope.row.deliveryType === 'LOGISTICS'" style="height:20px;font-size: 11px;line-height: 20px;padding: 0 16px;margin-bottom: 9px" color="#796CB4" @click="handleOpen(scope.row)">发货</v-btn>
                </div>
                <div class="flex_center">
                  <el-link :underline="false" class="table_detail" @click="handleClickDetail(scope.row.orderId)">订单详情</el-link>
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>
    <div class="pd_b_4" style="padding-right: 20px">
      <YTTablePagination
          ref="YTTablePaginationRef"
          :pageSize="$globalPageSize"
          :total="total"
          @page-change="handleChangePage"
      ></YTTablePagination>
    </div>
  </div>
  <el-dialog
      v-model="open"
      width="520"
      style="padding: 20px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false">
    <template #header>
      <div class="yt_dialog_title" style="padding-bottom: 30px">订单发货</div>
    </template>
    <div class="flex_center" style="margin-bottom: 21px">
      <div class="info_box">
        <div class="info_box_title mg_b_2">订单信息</div>
        <el-form-item label="平台订单号" label-width="90">
          <div class="info_box_goodsOrderNo">
            {{dialogForm.orderNo ? dialogForm.orderNo : "—"}}
          </div>
        </el-form-item>
        <el-form-item label="购买商品" label-width="90">
          <div class="w-100" style="padding-top: 6px;">
            <div v-for="(goods, index) in dialogForm.orderItemList" :key="`dialogOrderItemListName-${index}`" class="flex_bt flex_align_t" style="margin-bottom: 8px;">
              <div>
                <div class="info_box_goodsName">{{ goods.goodsName }}</div>
                <div class="flex_left" v-if="goods.properties.length > 0">
                  <div class="info_box_goodsProp">规格：</div>
                  <div class="info_box_goodsProp" v-for="(property, propIndex) in goods.properties" :key="`dialogProperties-${property.propertyGroupId}-propertyName-${property.propertyGroupItemId}-${propIndex}`">{{property.propertyName}}</div>
                </div>
              </div>
              <div class="info_box_goodsName">×{{ goods.quantity }}</div>
            </div>
          </div>
        </el-form-item>
        <el-form-item label="买家/收货人" label-width="90">
          <div class="flex_bt w-100" style="padding-top: 6px;">
            <div class="info_box_goodsAddress">
              <div style="text-align: left">{{dialogForm.receiverInfo?.receiverName ? dialogForm.receiverInfo?.receiverName : "—"}}&nbsp;{{`[` + (dialogForm.receiverInfo?.receiverPhone || '—') + `]`}}</div>
              <div style="text-align: left">{{dialogForm.receiverInfo?.receiverAddress ? dialogForm.receiverInfo?.receiverAddress : "—"}}</div>
            </div>
            <el-link :underline="false" class="table_detail" @click="handleDialogCopy(dialogForm)">复制</el-link>
          </div>
        </el-form-item>
        <el-form-item label="订单备注" label-width="90">
          <div class="info_box_goodsRemark">
            {{dialogForm.remark ? dialogForm.remark : "无"}}
          </div>
        </el-form-item>
      </div>
    </div>
    <div class="yt_dialog_container">
      <el-form
          ref="dialogFormRef"
          style="max-width: 385px;"
          :model="dialogForm"
          :rules="dialogRules"
          class="demo-ruleForm"
          status-icon
          v-loading="dialogLoading"
          id="dialogForm"
      >
        <el-form-item label="发货时间：" prop="deliveryTime">
          <el-date-picker
              v-model="dialogForm.deliveryTime"
              type="datetime"
              placeholder="请选择发货时间"
              style="width: 271px"
          />
        </el-form-item>
        <el-form-item label="配送快递：" prop="logisticsCompanyName">
          <el-select v-model="dialogForm.logisticsCompanyName" placeholder="请选择快递类型" style="width: 271px">
            <el-option
                v-for="item in logisticsCompanyOptions"
                :key="`logisticsCompanyOptions${item.value}`"
                :label="item.label"
                :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="物流单号：" prop="logisticsTrackingNo">
          <el-input style="width: 271px" v-model="dialogForm.logisticsTrackingNo" placeholder="请输入物流单号" />
        </el-form-item>
      </el-form>
    </div>
    <template #footer>
      <div class="yt_dialog_footer">
        <v-btn color="#d7d7df" variant="elevated" @click="handleClose">取消</v-btn>
        <v-btn class="mg_l_2" color="#4CAF50" variant="elevated" @click="handleSubmit">确定发货</v-btn>
      </div>
    </template>
  </el-dialog>
  <el-dialog
      v-model="openBatch"
      width="610"
      style="padding: 20px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false">
    <template #header>
      <div class="yt_dialog_title" style="padding-bottom: 30px">批量发货</div>
    </template>
    <div class="yt_dialog_container" style="padding: 0 45px;" v-loading="batchLoading">
      <el-upload
          ref="uploadRef"
          class="upload-demo"
          drag
          :auto-upload="false"
          :limit="1"
          accept=".xls,.xlsx"
          :on-change="handleFileChange"
          :on-remove="handleFileRemove"
          :before-upload="beforeUpload"
          :on-exceed="handleFileExceed"
      >
        <div class="flex_center" style="margin-bottom: 18px;">
          <img style="width: 26px;height: 26px;" src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/default_dragger_upload.svg"/>
        </div>
        <div class="dragger_upload_txt" style="margin-bottom: 17px;">
          点击或拖拽文件
        </div>
        <div class="flex">
          <div class="dragger_upload_tips">注：</div>
          <div class="dragger_upload_tips">
            <div>1、上传文件大小不超过10MB，仅支持xls和xlsx格式，单次导入数据不超过1000条记录。</div>
            <div>2、商户需 <span class="download_link" @click.stop="handleDownloadShippedOrders">导出待发货订单</span> 文件，且在文件上选择订单对应的配送快递类型（例如顺丰快递）和填写完整物流单号。</div>
            <div>3、可参考模板文件 <span class="download_link" @click.stop="handleDownLoadTempFile">批量上传物流单号文件示例</span> 来进行编辑导出待发货订单文件。</div>
          </div>
        </div>
        <template #tip>
        </template>
        <template #file="{ file }">
          <div class="flex_bt file_box">
            <div class="flex_center flex_left">
              <img class="mg_r_1" style="width: 18px;height: 20px;" src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/templateFiles/default_excel_icon.svg"/>
              <div class="file_name">{{file.name}}<span style="color: #6F717D;">({{formatFileSize(file.size)}})</span></div>
            </div>
            <div style="cursor: pointer" @click="handleFileRemove">
              <img style="width: 12px;height: 12px;" src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/file_upload_close.svg"/>
            </div>
          </div>
        </template>
      </el-upload>
    </div>
    <template #footer>
      <div class="yt_dialog_footer">
        <v-btn color="#d7d7df" variant="elevated" @click="handleBatchClose">取消</v-btn>
        <v-btn class="mg_l_2" :disabled="batchLoading" color="#4CAF50" variant="elevated" @click="handleBatchSubmit">确定</v-btn>
      </div>
    </template>
  </el-dialog>
  <el-drawer
      v-model="openBatchDrawer"
      direction="rtl"
      size="90%"
      :show-close="false"
      @close="handleCloseBatchDrawer"
  >
    <template #header>
      <div class="flex_left batch_title_box">
        <img @click="openBatchDrawer = false" style="width: 18px;height: 18px;margin-right: 13px;cursor: pointer;" src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/file_upload_close.svg"/>
        <div class="batch_title">批量发货</div>
      </div>
    </template>
    <div class="batch_filter mg_b_2" v-loading="drawerLoading">
      <div class=" flex_center flex_left" style="margin-bottom: 47px;">
        <div class="batch_filter_total mg_r_7">已上传{{drawerTotal}}个订单的物流单号</div>
        <v-btn color="#4CAF50" variant="elevated" @click="handleSubmitBatchShipments" style="padding: 0 34px;">已核对无误，确定发货</v-btn>
        <v-btn class="mg_l_2" color="#d7d7df" variant="elevated" @click="handleCancelBatchShipments" style="padding: 0 30px;">取消批量发货</v-btn>
      </div>
      <div class="flex_left flex_align_t">
        <div class="batch_filter_row" style="margin-right: 47px;">
          <el-input
              v-model="drawerForm.orderNo"
              :class="[
            drawerForm.orderNo === null || drawerForm.orderNo === ''
              ? 'input-null'
              : 'input-filled',
              'filter_input'
            ]"
          >
            <template #prefix>
              <div class="mg_r_1 filter_prefix">
                平台订单号
              </div>
            </template>
          </el-input>
          <el-input
              v-model="drawerForm.userId"
              :class="[
            drawerForm.userId === null || drawerForm.userId === ''
              ? 'input-null'
              : 'input-filled',
              'filter_input'
            ]"
          >
            <template #prefix>
              <div class="mg_r_1 filter_prefix">
                用户ID
              </div>
            </template>
          </el-input>
          <el-input
              v-model="drawerForm.userNickname"
              :class="[
            drawerForm.userNickname === null || drawerForm.userNickname === ''
              ? 'input-null'
              : 'input-filled',
              'filter_input'
            ]"
          >
            <template #prefix>
              <div class="mg_r_1 filter_prefix">
                用户昵称
              </div>
            </template>
          </el-input>
          <el-select
              :teleported="false"
              v-model="drawerForm.goodsId"
              :class="[
            drawerForm.goodsId === null || drawerForm.goodsId === ''
              ? 'input-null'
              : 'input-filled',
              'filter_order_status']"
              @change="handleDrawerGoodsChange"
              placeholder=""
              size="large"
              style="width: 240px;"
              :loading="goodsLoading"
              filterable
              remote
              remote-show-suffix
              :remote-method="handleRemoteSearch"
          >
            <template #prefix>
              <div class="mg_r_1 filter_prefix">
                商品名称
              </div>
            </template>
            <el-option
                style="text-align: left"
                v-for="item in goodsList"
                :key="`goodsId${item.goodsId}`"
                :label="item.goodsName"
                :value="item.goodsId"
            />
            <template v-if="!goodsLoading && !goodsList.some(res => res.goodsId === drawerForm.goodsId) && drawerForm.goodsId" #header>
              <div style="text-align: left">当前已选：</div>
              <el-option
                  style="text-align: left"
                  :label="drawerForm.goodsName"
                  :value="drawerForm.goodsId"
              />
            </template>

            <template #footer>
              <div class="flex_center">
                <el-pagination
                    size="small"
                    background
                    layout="prev, pager, next"
                    :total="totalGoods"
                    class="mt-4"
                    v-model:current-page="pageGoods"
                    v-model:page-size="pageSizeGoods"
                    @current-change="handleChangeGoodsPage"
                />
              </div>
            </template>
          </el-select>
          <div class="filter_time_group flex_center">
            <div class="filter_time_type_txt">下单时间</div>
            <el-date-picker
                :class="[
                drawerForm.orderTimeStart === null || drawerForm.orderTimeStart === ''
                ? 'input-null'
                : 'input-filled',
                'filter_start',
                'filter_date'
              ]"
                v-model="drawerForm.orderTimeStart"
                type="datetime"
                placeholder="开始时间"
                format="YYYY-MM-DD HH:mm:ss"
                @change="(val) => handleDrawerStartTimeChange(val, drawerForm.orderTimeEnd)"
                :teleported="false"
                style="border-left: none"
            />
            <div class="filter_separator">~</div>
            <el-date-picker
                :class="[
            drawerForm.orderTimeEnd === null ||
            drawerForm.orderTimeEnd === ''
              ? 'input-null'
              : 'input-filled',
              'filter_end',
              'filter_date'
            ]"
                v-model="drawerForm.orderTimeEnd"
                type="datetime"
                placeholder="结束时间"
                format="YYYY-MM-DD HH:mm:ss"
                :disabled-date="(time) => disabledEndDate(time, drawerForm.orderTimeStart)"
                :teleported="false"
            />
            <div class="filter_end_icon flex_center">
              <img class="cursor-pointer" style="width: 16px;height: 16px;" src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/default_calendar.svg" />
            </div>
          </div>
        </div>
        <div class="batch_filter_btn_group">
          <div class="batch_filter_btn primary" @click="getLogisticsInfo('btnSearch')">搜索</div>
          <div class="batch_filter_btn" @click="handleDrawerReset">重置</div>
        </div>
      </div>
    </div>
    <div class="batch_table" v-loading="drawerLoading">
      <el-table :data="drawerTableData" stripe style="width: 100%;border-bottom-left-radius: 15px;border-bottom-right-radius: 15px;margin-bottom: 32px;">
        <el-table-column prop="orderNo" label="平台订单号" width="180"/>
        <el-table-column prop="logisticsTrackingNo" label="物流单号" width="180"/>
        <el-table-column prop="orderTime" label="下单时间" width="180">
          <template #header>
            <div @click="handleDrawerOrderByChange" class="flex_center flex_left" style="text-align: center;cursor: pointer">
              <div>下单时间</div>
              <div>
                <div class="filter_orderTime_icon">
                  <el-icon :color="drawerOrderBy === 'ASC' ? '#9c9c9c' : '#d5d5d5'" :size="15"><CaretTop /></el-icon>
                </div>
                <div class="filter_orderTime_icon">
                  <el-icon :color="drawerOrderBy === 'DESC' ? '#9c9c9c' : '#d5d5d5'" :size="15"><CaretBottom /></el-icon>
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="userId" label="用户ID" width="180"/>
        <el-table-column prop="userNickname" label="昵称" width="120">
          <template #header>
            <div class="flex_center flex_left cursor-pointer" @click="handleShowNickname">
              <div style="margin-right: 8px">昵称</div>
              <img src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/default_eyes_open.svg" v-if="isShowNickname">
              <img src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/default_eyes_close.svg" v-else>
            </div>
          </template>
          <template #default="scope">
            <div class="flex_center flex_left" style="margin-bottom: 8px;">
              <div class="table_nickname">{{isShowNickname ? scope.row.userNickname : maskName(scope.row.userNickname)}}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column class-name="goods_cell_col" prop="goodsName" label="商品名称" min-width="280">
          <template #default="scope">
            <div :class="['goods_list']" :style="{
              ...(scope.row.orderItemList?.length > 1
                ? {
                    borderLeft: '1px solid #F0F0F6',
                    paddingTop: '0',
                    paddingBottom: '0',
                  }
                : {
                    borderLeft: 'none',
                    marginTop: '0',
                    marginBottom: '0'
                  })
            }">
              <div
                  v-for="(goods, index) in scope.row.orderItemList"
                  :key="`orderItemListNameDrawer${scope.$index}-${index}`"
                  :class="['goods_item', 'flex']"
                  :style="{paddingLeft: '10px',
                  marginLeft: 0}"
              >
                <div>
                  <el-tooltip
                      class="box-item"
                      effect="dark"
                      :content="handleTableGoodsNameStr(goods)"
                      placement="top"
                  >
                    <div class="goods_name">
                      {{ goods.goodsName }}
                      <span v-if="goods.properties.length > 0">
                        <span v-for="(property, propIndex) in goods.properties" :key="`propertiesDrawer-${property.propertyGroupId}-propertyName-${property.propertyGroupItemId}-${propIndex}`">
                          [{{ property.groupName }}:{{property.propertyName}}]
                        </span>
                      </span>
                    </div>
                  </el-tooltip>
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column class-name="goods_cell_col" prop="quantity" label="数量" width="120">
          <template #default="scope">
            <div class="goods_list" v-if="scope.row.orderItemList?.length > 0">
              <div class="goods_item flex_center" style="font-weight: 400;" v-for="(item, index) in scope.row.orderItemList" :key="`orderItemListQuantityDrawer${scope.$index}-${index}`">
                <div class="table_quantity">{{item.quantity}}</div>
              </div>
            </div>
            <div v-else>—</div>
          </template>
        </el-table-column>
        <el-table-column class-name="goods_cell_col" prop="address" label="单价" width="120">
          <template #default="scope">
            <div class="goods_list" v-if="scope.row.orderItemList?.length > 0" :style="{marginRight: '29px', borderRight: (scope.row.orderItemList?.length || 0) > 1 ? '1px solid #F0F0F6' : 'none'}">
              <div class="goods_item flex_center" v-for="(item, index) in scope.row.orderItemList" :key="`orderItemListPriceDrawer${scope.$index}-${index}`">
                <div>
                  <div class="table_price" style="margin-bottom: 6px;">会员价：￥{{item.retailPrice}}</div>
                  <div class="table_price">VIP价：￥{{item.vipPrice}}</div>
                </div>
              </div>
            </div>
            <div v-else>—</div>
          </template>
        </el-table-column>
        <el-table-column prop="receivedAmount" label="实收金额" width="120">
          <template #default="scope">
            <div class="flex">
              <div class="table_receivedAmount" style="font-weight: 400;">
                ￥{{scope.row.receivedAmount}}（{{scope.row.isFreeShipping ? '包邮' : '运费到付'}}）
              </div>
            </div>
          </template>
        </el-table-column>
      </el-table>
      <div class="pd_b_4" style="padding-right: 32px">
        <YTTablePagination
            ref="YTTablePaginationDrawerRef"
            :pageSize="$globalPageSize"
            :total="drawerTotal"
            @page-change="handleDrawerPageChange"
        ></YTTablePagination>
      </div>
    </div>
  </el-drawer>
  <el-drawer
      v-model="openDetailDrawer"
      direction="rtl"
      size="100%"
      :show-close="false"
      @close="handleCloseDetailDrawer"
  >
    <template #header>
      <div class="flex_left batch_title_box" style="padding-left: 40px;">
        <img @click="openDetailDrawer = false" style="width: 18px;height: 18px;margin-right: 13px;cursor: pointer;" src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/file_upload_close.svg"/>
        <div class="batch_title">订单详情</div>
      </div>
    </template>
    <div class="detail_state_box" :style="drawerDetailData?.orderState === 'PENDING_SHIPMENT' ? 'padding-bottom: 0;border: none;margin-bottom: 8px;' : ''">
      <div class="flex" :style="drawerDetailData?.orderState === 'PENDING_SHIPMENT' ? 'margin-bottom: 28px;' : 'margin-bottom: 42px;'" v-loading="detailDrawerLoading">
        <div class="detail_state_title_box mg_r_8">
          <div class="flex_align_t flex_left" v-if="drawerDetailData?.orderState === 'UNPAID'">
            <img class="detail_state_icon" style="margin-right: 15px;" src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/mall_detail_unpaid.svg"/>
            <div class="detail_state_title">等待买家付款</div>
          </div>
          <div class="flex_align_t flex_left" v-if="drawerDetailData?.orderState === 'SHIPPED' || drawerDetailData?.orderState ===  'RECEIVING'">
            <img class="detail_state_icon" style="margin-right: 15px;" src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/mall_detail_shipped.svg"/>
            <div class="detail_state_title">
              <div style="margin-bottom: 15px;">商家已发货，等待买家收货</div>
              <div class="detail_state_deliveryTime">{{ getOrderCompletedCountdown(drawerDetailData) }}</div>
            </div>

          </div>
          <div class="flex_align_t flex_left" v-if="drawerDetailData?.orderState === 'PENDING_SHIPMENT'">
            <img class="detail_state_icon" style="margin-right: 15px;" src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/mall_detail_paid.svg"/>
            <div class="detail_state_title">
              <div style="margin-bottom: 15px;">等待商家发货</div>
              <div class="detail_state_deliveryTime">{{ getDetailCountdown(drawerDetailData) }}</div>
            </div>
          </div>
          <div class="flex_align_t flex_left" v-if="drawerDetailData?.orderState === 'COMPLETED'">
            <img class="detail_state_icon" style="margin-right: 15px;" src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/mall_detail_completed.svg"/>
            <div class="detail_state_title">商品交易成功</div>
          </div>
          <div class="flex_align_t flex_left" v-if="drawerDetailData?.orderState === 'REFUNDED'">
            <img class="detail_state_icon" style="margin-right: 15px;" src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/mall_detail_close.svg"/>
            <div class="detail_state_title">商品交易关闭</div>
          </div>
          <div class="flex_align_t flex_left" v-if="drawerDetailData?.orderState === 'CANCELLED' || drawerDetailData?.orderState === 'CLOSED'">
            <img class="detail_state_icon" style="margin-right: 15px;" src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/mall_detail_cancel.svg"/>
            <div class="detail_state_title">
              <div style="margin-bottom: 15px;">买家取消支付</div>
              <div class="detail_state_time">取消时间：{{drawerDetailData.cancelTime || drawerDetailData.closeTime}}</div>
            </div>
          </div>
        </div>
        <div>
          <YTMallAnchor :isShowOverdue="isShowOverdue" :links="stepOptions"></YTMallAnchor>
        </div>
      </div>
      <div class="detail_state_tips" v-if="drawerDetailData?.orderState === 'PENDING_SHIPMENT' || drawerDetailData?.orderState === 'SHIPPED' || drawerDetailData?.orderState === 'RECEIVING' || drawerDetailData?.orderState === 'COMPLETED'">
        <span style="color: #684FA2;">BIKING UP 提醒</span>
        <span style="color: #888B9C;">
          <span v-if="drawerDetailData?.orderState === 'PENDING_SHIPMENT'">：如果无法发货，请及时与买家联系并说明情况后进行退款；</span>
          <span v-if="drawerDetailData?.orderState === 'SHIPPED' || drawerDetailData?.orderState === 'RECEIVING'">：请及时关注你发出的包裏状态，确保能配送至买家手中；如果买家表示未收到货或者货物有问题，请及时联系买家积极处理，友好协商；</span>
          <span v-if="drawerDetailData?.orderState === 'COMPLETED'">：交易已成功，如果买家提出售后要求，请积极与买家协商，做好售后服务；商品费用由 Biking Up 平台统一代收，最终结算金额平台将在适用的结算周期内结算，具体信息请查看您签署的《合作协议》。</span>
        </span>
      </div>
    </div>
    <div v-loading="detailDrawerLoading">
      <YTCard :title="`订单信息：`" style="margin-bottom: 25px;margin-left: 31px;">
        <template #container>
          <div class="detail_info_grid detail_info_box" style="max-width: 1760px;">
            <div class="detail_info_txt flex_left">
              <div class="detail_info_txt_label">平台单号：</div>
              <div>{{ drawerDetailData.orderNo }}</div>
            </div>

            <div class="detail_info_txt flex_left">
              <div class="detail_info_txt_label">下单时间：</div>
              <div>{{ drawerDetailData.orderTime }}</div>
            </div>

            <div class="detail_info_txt flex_left">
              <div class="detail_info_txt_label">支付方式：</div>
              <div v-if="drawerDetailData.payment?.paymentMethod === 'WXPAY'">微信支付</div>
              <div v-else-if="drawerDetailData.payment?.paymentMethod === 'ALIPAY'">支付宝支付</div>
              <div v-else>—</div>
            </div>

            <div class="detail_info_txt flex_left">
              <div class="detail_info_txt_label">送货方式：</div>
              <div v-if="drawerDetailData.deliveryType === 'LOGISTICS'">物流配送</div>
              <div v-else-if="drawerDetailData.deliveryType === 'NO_LOGISTICS'">无需物流</div>
              <div v-else>—</div>
            </div>

            <div class="detail_info_txt flex_left">
              <div class="detail_info_txt_label">订单备注：</div>
              <div>{{ drawerDetailData.remark }}</div>
            </div>

            <div class="detail_info_txt flex_left" v-if="drawerDetailData.cancelTime || drawerDetailData.closeTime">
              <div class="detail_info_txt_label">取消时间：</div>
              <div>{{ drawerDetailData.cancelTime || drawerDetailData.closeTime }}</div>
            </div>

            <div class="detail_info_txt flex_left" v-if="drawerDetailData.paymentTime">
              <div class="detail_info_txt_label">付款时间：</div>
              <div>{{ drawerDetailData.paymentTime }}</div>
            </div>

            <div class="detail_info_txt flex_left" v-if="drawerDetailData.deliveryTime">
              <div class="detail_info_txt_label">发货时间：</div>
              <div>{{ drawerDetailData.deliveryTime }}</div>
            </div>

            <div class="detail_info_txt flex_left" v-if="drawerDetailData.deliveryType === 'LOGISTICS' && (drawerDetailData?.logisticsCompanyName || drawerDetailData?.logisticsTrackingNo)">
              <div class="detail_info_txt_label">物流单号：</div>
              <div class="flex_left" v-if="drawerDetailData.deliveryType === 'LOGISTICS'">
                <div style="margin-right: 16px;">
                  {{ drawerDetailData.logisticsCompanyName ? drawerDetailData.logisticsCompanyName : "—" }}
                  {{ drawerDetailData.logisticsTrackingNo ? drawerDetailData.logisticsTrackingNo : "—" }}
                </div>
                <div class="flex flex_center cursor-pointer" style="gap: 6px;" @click="handleCopyTargetNo(drawerDetailData.logisticsTrackingNo)">
                  <img style="width: 13px;height: 13px;" src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/list_copy.svg"/>
                  <div style="color: #77788E;">复制</div>
                </div>
              </div>
            </div>

            <div class="detail_info_txt flex_left" v-if="drawerDetailData?.orderState === 'COMPLETED'">
              <div class="detail_info_txt_label">交易完成时间：</div>
              <div>{{ drawerDetailData.completionTime }}</div>
            </div>
          </div>
        </template>
      </YTCard>
      <YTCard :title="`买家信息：`" style="margin-bottom: 25px;margin-left: 31px;">
        <template #container>
          <div class="flex_left flex_align_t detail_info_box" style="gap: 140px;">
            <div class="detail_info_txt">
              <div class="flex_left" style="margin-bottom: 14px;">
                <div class="detail_info_txt_label">买家昵称：</div>
                <div>
                  <div class="flex_center flex_left">
                    <img style="width: 17px;height: 17px;border-radius: 50%;margin-right: 5px;" :src="drawerDetailData.userAvatar ? drawerDetailData.userAvatar : 'https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/default_avatar.svg'">
                    <div style="margin-right: 15px;">{{isShowDetailNickname ? drawerDetailData.nickname : maskName(drawerDetailData.nickname)}}</div>
                    <div class="flex cursor-pointer" style="user-select: none" @click="isShowDetailNickname = !isShowDetailNickname">
                      <img src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/default_eyes_open.svg" v-if="isShowDetailNickname">
                      <img src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/default_eyes_close.svg" v-else>
                      <div style="margin-left: 5px;color: #77788E;">查看</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="flex_left" style="margin-bottom: 14px;">
                <div class="detail_info_txt_label">买家ID号：</div>
                <div>{{drawerDetailData.userId}}</div>
              </div>
            </div>
            <div class="detail_info_txt">
              <div class="flex_left flex_align_t" style="margin-bottom: 14px;">
                <div class="detail_info_txt_label">收货信息：</div>
                <div>
                  <div class="flex" style="gap: 10px;margin-bottom: 10px">
                    <div>{{isShowDetailReceiverInfo ? drawerDetailData.receiverInfo?.receiverName : maskName(drawerDetailData.receiverInfo?.receiverName)}}</div>
                    <div class="flex flex_center cursor-pointer" style="user-select: none;gap: 5px;" @click="isShowDetailReceiverInfo = !isShowDetailReceiverInfo">
                      <img style="width: 18px;height: 9px;" src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/default_eyes_open.svg" v-if="isShowDetailReceiverInfo">
                      <img style="width: 18px;height: 9px;" src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/default_eyes_close.svg" v-else>
                      <div style="color: #77788E;">查看</div>
                    </div>
                    <div class="flex flex_center cursor-pointer" style="gap: 5px;" @click="handleCopy(drawerDetailData, isShowDetailReceiverInfo)">
                      <img style="width: 13px;height: 13px;" src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/table/table/list_copy.svg"/>
                      <div style="color: #77788E;">复制</div>
                    </div>
                  </div>
                  <div style="margin-bottom: 10px">{{isShowDetailReceiverInfo ? (drawerDetailData.receiverInfo?.receiverPhone || '') : maskPhone(drawerDetailData.receiverInfo?.receiverPhone)}}</div>
                  <div>{{isShowDetailReceiverInfo ? drawerDetailData.receiverInfo?.receiverAddress : maskAddressDetail(drawerDetailData.receiverInfo?.receiverAddress)}}</div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </YTCard>
      <YTCard :title="`商品信息：`" style="margin-left: 31px;">
        <template #container>
          <div style="text-align: right;margin-bottom: 15px;">所属商家：**{{drawerDetailData.tenantName}}—{{drawerDetailData.storeName}}</div>
          <el-table class="mg_b_3" :header-cell-style="{background: '#f5f6f7', fontFamily: 'OPPOSans', fontWeight: 500, fontSize: '14px', color: '#272539'}" :data="drawerDetailData.orderItemList" stripe style="width: 100%;">
            <el-table-column prop="goodsName" label="商品信息" min-width="180">
              <template #header>
                <div style="padding-left: 47px">商品信息</div>
              </template>
              <template #default="scope">
                <div :class="['goods_list']" :style="{
                   marginLeft: '29px'}">
                  <div :class="['goods_item', 'flex']"
                    :style="{paddingLeft: 0,
                    marginLeft: '10px'}">
                    <div style="width: 46px; height: 46px;margin-right: 15px;">
                      <el-image style="width: 46px; height: 46px;" :src="scope.row.goodsImage ? scope.row.goodsImage : ''" fit="contain" />
                    </div>
                    <div>
                      <el-tooltip
                          class="box-item"
                          effect="dark"
                          :content="handleTableGoodsNameStr(scope.row)"
                          placement="top"
                      >
                        <div class="detail_info_goodsName">
                          {{ scope.row.goodsName }}
                          <span v-if="scope.row.properties.length > 0">
                        <span v-for="(property, propIndex) in scope.row.properties" :key="`properties-${property.propertyGroupId}-propertyName-${property.propertyGroupItemId}-${propIndex}`">
                          [{{ property.groupName }}:{{property.propertyName}}]
                        </span>
                      </span>
                        </div>
                      </el-tooltip>
                      <div class="flex_left" style="gap: 3px;flex-wrap: wrap">
                        <div class="goods_item_tag" v-for="(tag, tagIndex) in scope.row.tagList" :key="`orderItemListName${scope.$index}-${index}-tagList${tagIndex}`">
                          {{tag.tagName}}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="" label="单价" min-width="120">
              <template #header>
                <div style="text-align: center">单价</div>
              </template>
              <template #default="scope">
                <div class="goods_list">
                  <div class="goods_item flex_center">
                    <div>
                      <div class="detail_info_txt" style="margin-bottom: 6px;">会员价：￥{{scope.row.retailPrice}}</div>
                      <div class="detail_info_txt">VIP价：￥{{scope.row.vipPrice}}</div>
                    </div>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="quantity" label="下单数量" width="180">
              <template #header>
                <div style="text-align: center">下单数量</div>
              </template>
              <template #default="scope">
                <div class="goods_list">
                  <div class="goods_item flex_center">
                    <div class="table_quantity">×{{scope.row.quantity}}</div>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="orderNo" label="支付状态" width="80">
              <template #header>
                <div style="text-align: center">支付状态</div>
              </template>
              <template #default="scope">
                <div :class="['goods_list']" :style="{marginRight: '29px', borderRight: (scope.row.length || 0) > 1 ? '1px solid #F0F0F6' : 'none'}">
                  <div class="table_orderItemState goods_list">
                    <div class="goods_item flex_center" style="padding-right: 0;margin-right: 10px;">
                      <div>
                        <YTMallOrderItemState :state="scope.row.orderItemState"></YTMallOrderItemState>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column v-if="isShowRefundInfo && drawerDetailData.orderItemList.length > 1" prop="refundItem.refundTime" label="退款时间" width="120">
              <template #default="scope">
                <div v-if="scope.row.refundItem?.refundTime">{{scope.row.refundItem.refundTime}}</div>
                <div v-else>—</div>
              </template>
            </el-table-column>
            <el-table-column v-if="isShowRefundInfo" prop="refundItem.refundReason" label="退款原因" width="120">
              <template #default="scope">
                <div v-if="scope.row.refundItem?.refundReason">{{scope.row.refundItem.refundReason}}</div>
                <div v-else>—</div>
              </template>
            </el-table-column>
            <el-table-column prop="payableAmount" label="实收金额" width="120">
              <template #header>
                <div style="text-align: center">实收金额</div>
              </template>
              <template #default="scope">
                <div class="detail_info_receivedAmount">
                  ￥{{scope.row.payableAmount}}
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="" label="优惠明细" min-width="120">
              <template #default="scope">
                <div class="detail_info_receivedAmount" style="text-align: left;" v-if="drawerDetailData.levelCode !== 'L1'">
                  VIP会员优惠：-￥{{Number(scope.row.retailPrice - scope.row.vipPrice)}}
                </div>
              </template>
            </el-table-column>
          </el-table>
          <div class="flex_right detail_info_box" style="gap: 25px;">
            <div class="detail_info_txt" style="font-size: 12px;">商品合计：￥{{drawerDetailData.originalAmount}}</div>
            <div class="detail_info_txt" style="font-size: 12px;">运费：￥{{drawerDetailData.deliveryFee}}</div>
            <div class="detail_info_txt" style="font-size: 12px;">优惠：￥{{drawerDetailData.discountAmount}}</div>
            <div class="detail_info_txt"><span style="color: #272539;">实付：</span><span style="font-size: 18px;color: #735DB4;font-weight: bold;">￥{{drawerDetailData.receivedAmount}}</span></div>
          </div>
          <div class="detail_info_partialRefund" style="margin-top: 15px;" v-if="drawerDetailData.isPartialRefund || drawerDetailData.refund">
            <span v-if="drawerDetailData.isPartialRefund">部分退款</span>
            <span v-if="drawerDetailData.refund">全额退款</span>
            成功 返还原支付账户金额: ￥{{drawerDetailData.refundAmount}}
          </div>
        </template>
      </YTCard>
    </div>
  </el-drawer>
</template>

<style lang="scss" scoped>
::v-deep(.ytCard_title) {
  font-weight: 500 !important;
  font-size: 17px !important;
  color: #272539 !important;
}
.form_txt_gray {
  font-weight: 500;
  font-size: 14px;
  color: #6f717d;
}
.drawer_table_filter_title {
  font-weight: 500;
  font-size: 17px;
  color: #272539;
}
::v-deep(.el-popover) {
  width: 170px !important;
  min-height: 40px !important;
  background: #ffffff !important;
  box-shadow: 0 1px 5px 0 rgba(25, 24, 27, 0.22) !important;
  border-radius: 12px;
  padding: 6px 0 !important;
}
.select_box {
  .select_opt {
    width: 100%;
    height: 37px;
    box-sizing: border-box;
    padding: 0 30px;
    cursor: pointer;
    line-height: 37px;
    border-bottom: 1px solid #ececf4;
    font-weight: 400;
    font-size: 14px;
    color: #4d4d4d;
    .select_active {
      color: #796cb4;
      font-weight: 500;
      font-size: 14px;
    }
  }
  .select_opt:last-child {
    border-bottom: none;
  }
  .select_opt:hover {
    background-color: #f5f7fa;
  }
}
.description_box {
  width: 100%;
  min-height: 150px;
  background: #ffffff;
  border-radius: 9px;
  border: 1px solid #796cb4;
  box-sizing: border-box;
  padding: 10px;
}

.tabs {
  .tabs_page {
    .view_statistics {
      font-size: 18px;
      color: #213547;
    }
    .img_box {
      display: flex;
      align-items: flex-start;
      .img_upload_info {
      }
      .img_list {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        .img_item_upload {
          border: none !important;
        }
        .img_item {
          position: relative;
          .del {
            width: 30px;
            height: 30px;

            position: absolute;
            top: -10px;
            right: -10px;
          }

          .image {
            width: 100px;
            height: 100px;
            object-fit: cover;
            object-position: center;
            background: #f6f6f6;
            border-radius: 20px;
            border: 1px solid rgba(0, 0, 0, 0.1);
          }
          .collect_count {
            border-radius: 25px;
            color: #213547;
            font-size: 12px;
            text-align: center;
          }
          .collect,
          .collected {
            width: 33px;
            height: 26px;

            position: absolute;
            bottom: 7px;
            right: 1px;
          }
        }
      }
    }
  }
}

// el-tabs
::v-deep(.el-tabs__item) {
  color: #999999;
  border-radius: 0 5px 0 0;
  width: 150px;
  padding: 0 20px !important;
  background-color: #f6f6f6;
}
::v-deep(.el-tabs__item.is-active) {
  color: #796cb4;
  background: linear-gradient(270deg, #eeeff6 0%, #f5f7fb 43%, #ffffff 100%);
  border-bottom: 2px solid #efefef;
}
::v-deep(.el-tabs__active-bar.is-top) {
  background-color: #796cb4;
}
::v-deep(.info_item) {
  width: 100%;
  background: #FFFFFF;
  border-radius: 15px;
  border: 1px solid #ECECF4;
  box-sizing: border-box;
  padding: 13px 20px;
  text-align: left;
  cursor: pointer;
  font-family: OPPOSans;
  .info_item_num {
    font-weight: 500;
    font-size: 23px;
    color: #2E2A4D;
    line-height: 20px;
    display: inline-block;
    margin-bottom: 8px;
  }
  .info_item_title {
    font-weight: 500;
    font-size: 15px;
    color: #656565;
    line-height: 16px;
  }
  .info_item_error {
    background: #ffe6e3;
    border-radius: 6px;
    font-weight: 500;
    font-size: 10px;
    color: #ef5353;
    line-height: 21px;
    box-sizing: border-box;
    padding: 0 8px;
  }
  &.active {
    background: #F8F6FF;
    border-radius: 15px;
    border: 1px solid #7A6DB4;
    font-weight: 500;
    color: #684FA2;
  }
}
::v-deep(.table_filter_box) {
  width: 100%;
  box-sizing: border-box;
  padding: 26px 63px 0 41px;
  .table_filter_title {
    font-weight: 500;
    font-size: 21px;
    color: #000000;
  }
}
::v-deep(.el-select-dropdown__list) {
  font-weight: 400;
  font-size: 14px;
  color: #4D4D4D;
  .is-selected {
    color: #796CB4;
    font-weight: 500;
    font-size: 14px;
  }
  /* 隐藏默认的多选对勾图标 */
  .is-selected::after {
    display: none;
  }
}
::v-deep(.el-select__wrapper) {
  min-height: 28px;
  height: 28px;
  font-size: 11px;
  border-radius: 6px;
}
::v-deep(.filter_type .el-select__wrapper) {
  box-shadow:
      1px 0 0 0 var(--el-border-color) inset,   /* 左 */
      0 1px 0 0 var(--el-border-color) inset,   /* 上 */
      0 -1px 0 0 var(--el-border-color) inset;  /* 下 */
  border-radius: 6px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  padding-right: 0;
  min-height: 28px;
}
::v-deep(.filter_start .el-input__wrapper) {
  text-align: center;
  box-shadow:
      0 1px 0 0 var(--el-border-color) inset,    /* 上 */
      0 -1px 0 0 var(--el-border-color) inset;   /* 下 */
  border-radius: 0;
  .el-input__prefix {
    display: none;
  }
  .el-input__inner {
    text-align: center;
  }
}
::v-deep(.filter_end .el-input__wrapper) {
  box-shadow:
      0 1px 0 0 var(--el-border-color) inset,    /* 上 */
      0 -1px 0 0 var(--el-border-color) inset;   /* 下 */
  border-radius: 0;
  .el-input__prefix {
    display: none;
  }
  .el-input__inner {
    text-align: center;
  }
}
.filter_end_icon {
  height: 28px;
  box-shadow:
      -1px 0 0 0 var(--el-border-color) inset,   /* 右 */
      0 1px 0 0 var(--el-border-color) inset,   /* 上 */
      0 -1px 0 0 var(--el-border-color) inset;  /* 下 */
  padding-right: 11px;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}
::v-deep(.el-input),
::v-deep(.el-input__wrapper) {
  height: 28px;
  border-radius: 6px;
}
::v-deep(.v-btn--variant-elevated) {
  box-shadow: none;
  transition: .3s;
}
::v-deep(.v-btn) {
  height: 28px;
}
/*过滤器适配屏幕*/
::v-deep(.filter_row) {
  flex-wrap: wrap;
  gap: 10px;
  .filter_input {
    width: clamp(155px, 10vw, 240px);
    font-size: 11px;
  }
  .filter_time_group {
    height: 28px;
  }
  .filter_time_type {
    width: 80px;
    height: 28px;
    font-size: 11px;
  }
  .filter_date {
    width: clamp(132px, 9vw, 220px)!important;
    font-size: 11px!important;
  }
  .filter_order_status {
    width: clamp(150px, 10vw, 260px);
    font-size: 11px;
  }
  .filter_prefix,
  .filter_option {
    font-size: 11px;
  }
  .filter_separator {
    font-size: 11px;
    line-height: 28px;
    color: #dcdfe6;
    height: 28px;
    border-top: 1px solid #dcdfe6;
    border-bottom: 1px solid #dcdfe6;
  }
  .selected_icon {
    width: 11px;
    height: 11px;
  }
}
.filter_total_num {
  font-family: OPPOSans;
  font-weight: 500;
  font-size: 14px;
  color: #656565;
}
.filter_line {
  width: 1px;
  height: 19px;
  background: #DADDE2;
  margin: 0 14px;
}
.filter_orderTime {
  font-family: OPPOSans;
  font-weight: 500;
  font-size: 12px;
  color: #656565;
  line-height: 14px;
  user-select: none;
}
.filter_orderTime_icon {
  width: 24px;
  height: 7px;
  display: flex;
  justify-content: center;
  align-items: center;
}
::v-deep(.el-table .order_header) {
  height: 32px;
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #888B9C;
  background: #F7F8FA;
}

::v-deep(.el-table__row:has(.order_header) td){
  background: #F7F8FA;
}
.table_price {
  font-family: OPPOSans;
  font-weight: 500;
  font-size: 12px;
  color: #656565;
  line-height: 12px;
}
.table_quantity {
  font-family: OPPOSans;
  font-weight: bold;
  font-size: 12px;
  color: #272539;
  line-height: 12px;
}
.table_orderItemState {
  font-weight: 500;
  font-size: 12px;
  text-align: center;
  .isSuccess {
    color: #67C23A;
  }
  .isCancel {
    color: #94979D;
  }
}
.table_paymentType,
.table_isFreeShipping,
.table_remark {
  font-family: OPPOSans;
  font-weight: 400;
  font-size: 12px;
  color: #656565;
  text-align: center;
}
.table_receivedAmount {
  font-family: OPPOSans;
  font-weight: bold;
  font-size: 12px;
  color: #272539;
  text-align: center;
}
.table_nickname {
  font-family: OPPOSans;
  font-weight: 500;
  font-size: 12px;
  color: #656565;
  text-align: center;
  line-height: 17px;
}
.table_customerName,
.table_address {
  font-family: OPPOSans;
  font-weight: 500;
  font-size: 11px;
  color: #656565;
  text-align: left;
  line-height: 12px;
}
.table_state {
  font-family: OPPOSans;
  font-weight: 500;
  font-size: 12px;
  color: #272539;
  line-height: 12px;
  .isSuccess {
    color: #67C23A;
  }
  .isCancel {
    color: #94979D;
  }
  .countdown {
    font-size: 11px;
    color: #FF4847;
    line-height: 11px;
    margin-top: 8px;
  }
}
.table_detail {
  font-family: OPPOSans;
  font-weight: 500;
  font-size: 12px;
  color: #684FA2;
  line-height: 12px;
  cursor: pointer;
  &.table_detail:hover {
    text-decoration: underline;
  }
}
.goods_list {
  width: 100%;
  font-family: OPPOSans;
  .detail_info_txt {
    font-weight: 500;
    font-size: 12px;
    color: #6F717D;
    line-height: 12px;
  }
  .detail_info_goodsName {
    font-weight: 500;
    font-size: 12px;
    color: #272539;
    line-height: 17px;
  }
  .detail_info_receivedAmount {
    font-weight: 500;
    font-size: 12px;
    color: #6F717D;
    line-height: 12px;
    text-align: center;
  }
}

.goods_item {
  min-height: 72px;
  padding: 10px 8px;
  box-sizing: border-box;
  border-bottom: 1px solid #F0F0F6;
  font-size: 12px;
  color: #303133;

  .goods_item_border_left {
    border-left: 1px solid #F0F0F6;
  }
  .goods_name {
    width: 250px;
    white-space: normal;
    line-height: 18px;

    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-all;
  }

  .goods_item_tag {
    background: #F5F1FF;
    border-radius: 3px;
    line-height: 13px;
    padding: 0 5px;
    font-family: OPPOSans;
    font-weight: 400;
    font-size: 10px;
    color: #B292F1;
  }

  &:last-child {
    border-bottom: none;
  }
}

.order_header {
  height: 32px;
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #606266;

  .order_header_time {
    margin-left: 40px;
  }
}

:deep(.goods_cell_col) {
  padding: 0 !important;
  width: 100%;
  .cell {
    width: 100%;
    padding: 0 !important;
    height: 100%;
  }
}
/*弹窗标题*/
.yt_dialog_title {
  padding: 20px 0 17px 33px;
  font-weight: 500;
  font-size: 19px;
  color: #272539;
  text-align: left;
}
/*弹窗内容*/
.yt_dialog_container {
  padding-left: 33px;
  text-align: left;
  ::v-deep(.el-select__wrapper),
  ::v-deep(.el-input__wrapper) {
    height: 34px;
    font-size: 12px;
    color: #6F717D;
    background: #FFFFFF;
    border-radius: 9px;
    border: 1px solid #E8E8E9;
  }
}
/*弹窗底部按钮栏*/
.yt_dialog_footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin: 0 33px 33px 0;
}
.info_box {
  width: 100%;
  min-height: 260px;
  background: #F5F7FA;
  border-radius: 10px;
  box-sizing: border-box;
  padding: 21px;
  margin: 0 24px;
  text-align: left;
  font-family: OPPOSans;
  .info_box_title {
    font-weight: 500;
    font-size: 15px;
    color: #272539;
    line-height: 15px;
  }
  .info_box_goodsOrderNo,
  .info_box_goodsName,
  .info_box_goodsAddress,
  .info_box_goodsRemark {
    font-weight: 400;
    font-size: 12px;
    color: #434555;
    line-height: 18px;
  }
  .info_box_goodsProp {
    font-weight: 400;
    font-size: 10px;
    color: #737373;
    line-height: 13px;
    margin-left: 3px;
    &:first-child {
      margin-left: 0;
    }
  }
  ::v-deep(.el-form-item__label) {
    font-family: OPPOSans;
    font-weight: 400;
    font-size: 12px;
    color: #434555;
    line-height: 32px;
  }
}
::v-deep(.el-upload-dragger) {
  border-color: rgba(25,24,27,0.06);
  border-radius: 10px;
  background: #F9FAFD;
  padding: 29px 24px;
}
::v-deep(.el-upload-dragger:hover) {
  border-color: #272539;
  color: #272539;
}
.dragger_upload_txt {
  font-family: OPPOSans;
  font-weight: 500;
  font-size: 19px;
  color: #272539;
  line-height: 19px;
  text-align: center;
}
.dragger_upload_tips {
  font-family: OPPOSans;
  font-weight: 400;
  font-size: 11px;
  color: #9398A9;
  line-height: 17px;
  text-align: left;
  .download_link {
    color: #684FA2;
    &:hover {
      text-decoration: underline;
    }
  }
}
.file_box {
  height: 32px;
  background: #FFFFFF;
  border-radius: 6px;
  border: 1px solid #DADDE2;
  box-sizing: border-box;
  padding: 0 19px;
  .file_name {
    font-family: OPPOSans;
    font-weight: 400;
    font-size: 12px;
    color: #000000;
    line-height: 32px;
  }
}
.batch_title_box{
  font-family: OPPOSans;
  box-sizing: border-box;
  padding: 55px 0 0 15px;
  margin-bottom: 46px;
  .batch_title {
    font-weight: 500;
    font-size: 21px;
    color: #000000;
    line-height: 21px;
  }
}
.batch_filter {
  box-sizing: border-box;
  padding-left: 17px;
  font-family: OPPOSans;
  ::v-deep(.el-input__wrapper) {
    height: 36px;
  }
  .batch_filter_total {
    font-weight: bold;
    font-size: 23px;
    color: #222222;
    line-height: 23px;
  }
  .batch_filter_row {
    display: flex;
    flex-wrap: wrap;
    column-gap: 28px; // 横向间距
    row-gap: 15px; // 纵向间距
    .filter_input {
      width: clamp(200px, 10vw, 240px);
      font-size: 11px;
    }
    .filter_time_group {
      height: 36px;
    }
    .filter_time_type_txt {
      height: 36px;
      box-sizing: border-box;
      font-weight: 500;
      font-size: 14px;
      color: #9C9EAB;
      line-height: 36px;
      box-shadow:
          1px 0 0 0 var(--el-border-color) inset,   /* 左 */
          0 1px 0 0 var(--el-border-color) inset,   /* 上 */
          0 -1px 0 0 var(--el-border-color) inset;  /* 下 */
      padding-left: 21px;
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
    }
    .filter_time_type {
      width: 80px;
      height: 36px;
      font-size: 14px;
    }
    .filter_date {
      width: clamp(132px, 9vw, 220px)!important;
      font-size: 14px!important;
    }
    .filter_order_status {
      width: clamp(200px, 10vw, 260px);
      font-size: 14px;
    }
    .filter_prefix,
    .filter_option {
      font-size: 14px;
    }
    .filter_separator {
      box-sizing: border-box;
      text-align: center;
      font-size: 14px;
      line-height: 34px;
      color: #9C9EAB;
      border-top: 1px solid #dcdfe6;
      border-bottom: 1px solid #dcdfe6;
    }
    .selected_icon {
      width: 11px;
      height: 11px;
    }
    .filter_end_icon {
      height: 36px;
    }
    ::v-deep(.el-input),
    ::v-deep(.el-input__wrapper) {
      height: 36px;
    }
    ::v-deep(.el-select__wrapper) {
      height: 36px;
      font-size: 14px;
      border-radius: 6px;
    }
  }
  .batch_filter_btn_group {
    display: flex;
    gap: 14px;
    flex-shrink: 0;
    align-items: center;
    justify-content: flex-end;
    .batch_filter_btn {
      min-width: 60px;
      height: 36px;
      background: #FFFFFF;
      border-radius: 6px;
      border: 1px solid #DADDE2;
      box-sizing: border-box;
      padding: 0 16px;
      font-family: OPPOSans;
      font-weight: 500;
      font-size: 14px;
      color: #272539;
      line-height: 36px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      white-space: nowrap;
      flex-shrink: 0;
      transition: .3s;
      &.primary {
        color: #684FA2;
        border-color: #7A6DB4;
      }
      &:hover {
        transition: .3s;
        box-shadow: 0px 2px 4px -1px var(--v-shadow-key-umbra-opacity, rgba(0, 0, 0, 0.2)), 0px 4px 5px 0px var(--v-shadow-key-penumbra-opacity, rgba(0, 0, 0, 0.14)), 0px 1px 10px 0px var(--v-shadow-key-ambient-opacity, rgba(0, 0, 0, 0.12));
      }
    }
  }
}
.batch_table {
  padding-left: 17px;
  ::v-deep(.el-table__header) {
    border-top-left-radius: 15px;
    border-top-right-radius: 15px;
    thead {
      tr:first-child {
        th:first-child {
          border-top-left-radius: 15px;
        }
        th:last-child {
          border-top-right-radius: 15px;
        }
      }
    }
  }
}
::v-deep(.el-select-dropdown__item.is-selected) {
  color: #796CB4;
  font-weight: 500;
  font-size: 14px;
}
.detail_state_box {
  box-sizing: border-box;
  padding: 0 0 32px 0;
  margin-left: 65px;
  border-bottom: 2px solid #E8EBF0;
  font-family: OPPOSans;
  .detail_state_title_box {
    box-sizing: border-box;
    padding: 35px 82px 0 0;
    border-right: 2px solid #E8EBF0;
    .detail_state_icon {
      width: 32px;
      height: 32px;
      margin-top: 11px;
    }
    .detail_state_title {
      height: 138px;
      font-weight: 500;
      font-size: 36px;
      color: #272539;
    }
    .detail_state_time {
      font-weight: 500;
      font-size: 13px;
      color: #676767;
      line-height: 15px;
    }
    .detail_state_deliveryTime {
      font-weight: 500;
      font-size: 14px;
      color: #FF4847;
      line-height: 15px;
      text-align: left;
    }
  }
  .detail_state_tips {
    width: 100%;
    min-height: 32px;
    background: #F7F8FA;
    border: 1px solid #DADDE2;
    font-family: OPPOSans;
    font-weight: 500;
    font-size: 13px;
    color: #684FA2;
    line-height: 32px;
    text-align: left;
    box-sizing: border-box;
    padding: 0 28px;
  }
}
.detail_info_box {
  font-family: OPPOSans;
  .detail_info_txt {
    font-weight: 500;
    font-size: 14px;
    color: #6F717D;
    .detail_info_txt_label {
      white-space: nowrap;
      flex-shrink: 0;
    }
  }
}
.detail_info_partialRefund {
  font-weight: 500;
  font-size: 14px;
  color: #FF4847;
  line-height: 14px;
  text-align: right;
}
.detail_info_grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(330px, max-content));
  column-gap: 140px;
  row-gap: 14px;
  align-items: start;
}
:deep(.el-table.outside_table.el-table--striped .el-table__body tr.el-table__row--striped td.el-table__cell) {
  background: #fff;
}
</style>
